import React, { useEffect, useMemo, useRef, useState } from "react";
import imageCompression from "browser-image-compression";
import ReactDOM from "react-dom";
import { QRCodeSVG } from "qrcode.react";
import { collection, doc, getDocs, onSnapshot, updateDoc, addDoc, serverTimestamp, getDoc, setDoc, writeBatch, query, where, deleteDoc, increment } from "firebase/firestore";
import { onAuthStateChanged, signInWithCustomToken } from "firebase/auth";
import { db, auth } from "../firebase";
import { CATEGORIES } from "../constants/categories";

// Minimal inline CSV parser (avoids papaparse Node.js compatibility issues)
function parseCsv(text) {
  const records = [];
  let row = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    const next = text[i + 1];

    if (ch === '"') {
      if (inQuotes && next === '"') {
        cell += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      row.push(cell);
      cell = "";
    } else if ((ch === "\n" || ch === "\r") && !inQuotes) {
      if (ch === "\r" && next === "\n") i += 1;
      row.push(cell);
      if (row.some((value) => value.trim())) records.push(row);
      row = [];
      cell = "";
    } else {
      cell += ch;
    }
  }

  row.push(cell);
  if (row.some((value) => value.trim())) records.push(row);

  if (!records.length) return { headers: [], rows: [] };
  const headers = records[0].map((h) => h.trim().replace(/^"|"$/g, "").toLowerCase());
  const rows = records.slice(1).map((record) => {
    const rowObj = {};
    headers.forEach((h, i) => { rowObj[h] = (record[i] ?? "").trim(); });
    return rowObj;
  });
  return { headers, rows };
}

function csvValue(row, keys) {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && String(value).trim()) return String(value).trim();
  }
  return "";
}

function parseCsvNumber(value, fallback = 0) {
  const parsed = parseFloat(String(value || "").replace(/[₹,\s]/g, ""));
  return Number.isFinite(parsed) ? parsed : fallback;
}

const API_BASE   = "https://kiranaos-api-1036279469035.asia-south1.run.app";

const STATUS_ORDER = ["pending", "accepted", "preparing", "out_for_delivery", "delivered", "cancelled"];
const STATUS_LABEL = {
  pending:          "New",
  accepted:         "Accepted",
  preparing:        "Packing",
  out_for_delivery: "Out for Delivery",
  delivered:        "Delivered",
  cancelled:        "Cancelled",
};
const STATUS_NEXT = {
  pending:          "accepted",
  accepted:         "preparing",
  preparing:        "out_for_delivery",
  out_for_delivery: "delivered",
};
const STATUS_NEXT_LABEL = {
  pending:          "Accept Order",
  accepted:         "Mark Packing",
  preparing:        "Out for Delivery",
  out_for_delivery: "Mark Delivered",
};

// CATEGORIES is the shared canonical taxonomy from constants/categories.js
// (also used by the customer storefront) so the two screens never drift apart.

// ─── i18n ─────────────────────────────────────────────────────────────────────
// Centralised translation table. Add keys here first; never scatter raw strings
// across components. English is the authoritative fallback for any missing key.
const TRANSLATIONS = {
  en: {
    // Language picker
    langPickerHeading: "Which language are you comfortable with?",
    langPickerSub:     "आप किस भाषा में काम करना पसंद करते हैं?",
    langPickerFooter:  "You can change this later in Settings.",
    langEnglish:       "English",
    langHindi:         "हिन्दी",
    // Top bar
    commandCenter:     "KiranaOS Command Center",
    // Tab labels
    tabToday:          "Today",
    tabOrders:         "Orders",
    tabAnalytics:      "Analytics",
    tabInventory:      "Inventory",
    tabDiscounts:      "Discounts",
    tabSettings:       "Settings",
    // Settings — store info
    storeInformation:  "Store Information",
    storeName:         "Store Name",
    phoneNumber:       "Phone Number",
    storeAddress:      "Store Address",
    storeCity:         "City",
    storeHours:        "Store Hours",
    saveSettings:      "Save Settings",
    saving:            "Saving…",
    settingsSaved:     "✓ Settings saved",
    failedSave:        "Failed to save. Check Firestore permissions.",
    // Settings — QR
    storeQR:           "Store QR Code",
    scanQR:            "Scan to view your store menu",
    downloadQR:        "Download QR Code",
    // Settings — language preference
    languageSection:   "Language Preference",
    chooseLanguage:    "Dashboard language",
    // Inventory
    addItem:           "Add Item",
    addItemModalTitle: "Add Item",
    productName:       "Product name *",
    priceLabel:        "Price (₹) *",
    unitLabel:         "Unit *",
    categoryLabel:     "Category",
    stockLabel:        "Initial stock",
    availabilityLabel: "Available",
    productImage:      "Product image (optional)",
    tapToAddPhoto:     "Tap to add photo",
    photoReady:        "✓ Photo added",
    saveItem:          "Save",
    saveAddAnother:    "Save & Add Another",
    cancel:            "Cancel",
    bulkUpload:        "Bulk upload",
    downloadTemplate:  "Download template",
    couldNotAdd:       "Could not add item. Please try again.",
    // Quick tour
    quickTour:         "Quick Tour",
    tourWelcomeTitle:  "Your shop is now online",
    tourWelcomeBody:   "Customers can scan your QR or open your store link and order. Do these 4 things first, it takes about 5 minutes.",
    tourTodayTitle:    "First: share your store link",
    tourTodayBody:     "Tap Share and send your store link to regular customers on WhatsApp. Orders can start today.",
    tourOrdersTitle:   "When an order comes",
    tourOrdersBody:    "New customer orders appear here. Tap Accept, then pack and deliver from the same screen.",
    tourInventoryTitle:"Check your products and prices",
    tourInventoryBody: "Your store starts with ready-made products. Fix prices and stock, add photos, and switch off items you do not keep.",
    tourSettingsTitle: "Print your QR for the counter",
    tourSettingsBody:  "This is Settings — the gear (⚙) icon at the top right on mobile. Download the QR code here and stick it at your counter. Customers scan it and order, no app needed.",
    tourBack:          "Back",
    tourNext:          "Next",
    tourDone:          "Finish tour",
    tourSkip:          "Skip",
    askKiran:          "Help",
    helpTitle:         "Ask Kiran",
    helpSubtitle:      "Quick help for common dashboard questions.",
    helpClose:         "Close",
  },
  hi: {
    // Language picker
    langPickerHeading: "आप किस भाषा में काम करना पसंद करते हैं?",
    langPickerSub:     "Which language are you comfortable with?",
    langPickerFooter:  "आप इसे बाद में Settings में बदल सकते हैं।",
    langEnglish:       "English",
    langHindi:         "हिन्दी",
    // Top bar
    commandCenter:     "KiranaOS कमांड सेंटर",
    // Tab labels
    tabToday:          "आज",
    tabOrders:         "ऑर्डर",
    tabAnalytics:      "विश्लेषण",
    tabInventory:      "सामान",
    tabDiscounts:      "छूट",
    tabSettings:       "सेटिंग",
    // Settings — store info
    storeInformation:  "दुकान की जानकारी",
    storeName:         "दुकान का नाम",
    phoneNumber:       "फोन नंबर",
    storeAddress:      "दुकान का पता",
    storeCity:         "शहर",
    storeHours:        "खुलने का समय",
    saveSettings:      "सेटिंग सहेजें",
    saving:            "सहेज रहे हैं…",
    settingsSaved:     "✓ सेटिंग सहेजी गई",
    failedSave:        "सहेजने में त्रुटि। Firestore permissions जांचें।",
    // Settings — QR
    storeQR:           "दुकान का QR कोड",
    scanQR:            "अपना स्टोर मेनू देखने के लिए स्कैन करें",
    downloadQR:        "QR कोड डाउनलोड करें",
    // Settings — language preference
    languageSection:   "भाषा प्राथमिकता",
    chooseLanguage:    "डैशबोर्ड की भाषा",
    // Inventory
    addItem:           "सामान जोड़ें",
    addItemModalTitle: "सामान जोड़ें",
    productName:       "सामान का नाम *",
    priceLabel:        "दाम (₹) *",
    unitLabel:         "इकाई *",
    categoryLabel:     "श्रेणी",
    stockLabel:        "शुरुआती स्टॉक",
    availabilityLabel: "उपलब्ध",
    productImage:      "सामान की फोटो (वैकल्पिक)",
    tapToAddPhoto:     "फोटो जोड़ें",
    photoReady:        "✓ फोटो जुड़ गई",
    saveItem:          "सहेजें",
    saveAddAnother:    "सहेजें और एक और जोड़ें",
    cancel:            "रद्द करें",
    bulkUpload:        "एक साथ अपलोड",
    downloadTemplate:  "टेम्पलेट डाउनलोड करें",
    couldNotAdd:       "सामान नहीं जोड़ा जा सका। दोबारा कोशिश करें।",
    // Quick tour
    quickTour:         "टूर देखें",
    tourWelcomeTitle:  "आपकी दुकान अब ऑनलाइन है",
    tourWelcomeBody:   "ग्राहक QR स्कैन करके या लिंक खोलकर ऑर्डर कर सकते हैं। पहले ये 4 काम करें, सिर्फ 5 मिनट लगेंगे।",
    tourTodayTitle:    "पहला काम: दुकान का लिंक भेजें",
    tourTodayBody:     "Share दबाकर अपने रोज़ के ग्राहकों को WhatsApp पर लिंक भेजें। ऑर्डर आज से ही आ सकते हैं।",
    tourOrdersTitle:   "नया ऑर्डर आने पर",
    tourOrdersBody:    "नए ऑर्डर यहाँ दिखेंगे। Accept दबाएँ, फिर उसी स्क्रीन से पैक और डिलीवर करें।",
    tourInventoryTitle:"अपना सामान और दाम जाँचें",
    tourInventoryBody: "दुकान में पहले से सामान जुड़ा है। दाम और स्टॉक ठीक करें, फोटो जोड़ें, और जो चीज़ नहीं रखते उसे बंद कर दें।",
    tourSettingsTitle: "QR प्रिंट करके काउंटर पर लगाएँ",
    tourSettingsBody:  "यह Settings है — मोबाइल पर ऊपर दाईं ओर gear (⚙) आइकॉन। यहाँ से QR कोड डाउनलोड करें और काउंटर पर लगाएँ। ग्राहक स्कैन करके ऑर्डर करेंगे।",
    tourBack:          "पीछे",
    tourNext:          "आगे",
    tourDone:          "हो गया",
    tourSkip:          "छोड़ें",
    askKiran:          "Help",
    helpTitle:         "Ask Kiran",
    helpSubtitle:      "Dashboard ke common sawaalon ka quick help.",
    helpClose:         "Close",
  },
};

// Returns the translation for `key` in `lang`, falling back to English.
function tr(lang, key) {
  return (TRANSLATIONS[lang] || TRANSLATIONS.en)[key] ?? TRANSLATIONS.en[key] ?? key;
}

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  white:       "#FFFFFF",
  offWhite:    "#F6F7FB",
  black:       "#101113",
  inkSoft:     "#2A2D33",
  grey50:      "#FBFBFD",
  grey100:     "#ECEEF3",
  grey200:     "#E2E5EC",
  grey300:     "#C9CED8",
  grey500:     "#737A86",
  grey600:     "#4F5663",
  orange:      "#E8470A",
  orangeLight: "#FDECE4",
  green:       "#1A6B3C",
  blue:        "#C23608", // was #2563EB - brand doc bans blue
  violet:      "#9B1C1C", // was #7C3AED - brand doc bans purple
  amber:       "#D4A017",
  red:         "#DC2626",
  shadowSm:    "0 1px 2px rgba(16,17,19,0.05), 0 1px 8px rgba(16,17,19,0.04)",
  shadowMd:    "0 12px 32px rgba(16,17,19,0.08)",
  shadowLg:    "0 24px 70px rgba(16,17,19,0.16)",
  radiusSm:    8,
  radiusMd:    12,
  radiusLg:    16,
  radiusPill:  100,
  ease:        "cubic-bezier(0.4, 0, 0.2, 1)",
  fontBody:    "'DM Sans', -apple-system, sans-serif",
  fontDisplay: "'Fraunces', Georgia, serif",
};

// ─── Auth helpers ─────────────────────────────────────────────────────────────
// Wraps fetch with a single token-refresh retry on 401/403.
// Firebase ID tokens expire every hour. getIdToken() returns the cached copy,
// which can already be expired. On a 401/403 response we force-refresh once
// and retry before giving up. This prevents the owner from seeing "session
// expired" errors mid-session just because the token clock ticked over.
async function fetchWithAuthRetry(url, options = {}) {
  const getToken = (forceRefresh = false) =>
    auth.currentUser ? auth.currentUser.getIdToken(forceRefresh) : Promise.resolve(null);

  const token = await getToken(false);
  const headers = { ...(options.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let res = await fetch(url, { ...options, headers });

  if ((res.status === 401 || res.status === 403) && auth.currentUser) {
    try {
      const freshToken = await getToken(true);
      if (freshToken) {
        headers["Authorization"] = `Bearer ${freshToken}`;
        res = await fetch(url, { ...options, headers });
      }
    } catch (refreshErr) {
      console.warn("[KiranaOS] Owner token refresh failed:", refreshErr.message);
    }
  }

  return res;
}

// ─── Image upload helper ──────────────────────────────────────────────────────
// Prepares a user-selected or camera-captured photo for the product image API.
// - JPEG/PNG/WebP: compress with browser-image-compression (existing behavior).
//   If compression throws, fall back to the original file when it is already a
//   backend-supported type and <= 10 MB (the backend's multer limit).
// - HEIC/HEIF (common for iPhone camera photos): convert to JPEG first via a
//   dynamically imported converter, then compress. browser-image-compression
//   cannot decode HEIC on its own, which is why raw camera uploads failed.
// Throws an Error carrying a merchant-facing message when the photo is unusable.
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const BACKEND_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

async function prepareImageForUpload(file) {
  const type = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  const isHeic = type === "image/heic" || type === "image/heif"
    || name.endsWith(".heic") || name.endsWith(".heif");

  let source = file;
  let filename = file.name || "photo.jpg";

  if (isHeic) {
    try {
      // Dynamic import keeps the converter out of the main dashboard bundle.
      const heic2any = (await import("heic2any")).default;
      const out = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
      const jpegBlob = Array.isArray(out) ? out[0] : out;
      filename = filename.replace(/\.(heic|heif)$/i, ".jpg");
      if (!/\.jpe?g$/i.test(filename)) filename += ".jpg";
      source = new File([jpegBlob], filename, { type: "image/jpeg" });
    } catch (err) {
      console.error("HEIC conversion failed:", err);
      throw new Error("This camera photo format could not be converted. Please choose another photo or take the photo in Most Compatible mode.");
    }
  }

  try {
    const compressed = await imageCompression(source, { maxSizeMB: 0.5, maxWidthOrHeight: 800, useWebWorker: true });
    return { blob: compressed, filename };
  } catch (err) {
    console.error("Image compression failed:", err);
    // Compression can fail on odd frames; the original is fine if already supported and within the size cap.
    const sourceType = (source.type || "").toLowerCase();
    if (BACKEND_IMAGE_TYPES.has(sourceType) && source.size <= MAX_UPLOAD_BYTES) {
      return { blob: source, filename };
    }
    throw new Error("Could not process this image. Please try a different photo (JPG or PNG).");
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function timeAgo(ts) {
  if (!ts) return "";
  const secs = Math.floor((Date.now() - ts.toMillis()) / 1000);
  if (secs < 60)    return `${secs}s ago`;
  if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}
function fmt(n) {
  if (n == null) return "—";
  return `₹${Number(n).toFixed(2).replace(/\.00$/, "")}`;
}

function getStoreUrl(storeId) {
  return `https://kiranaos.in/store/${storeId}`;
}

function getShareText(storeName, storeUrl) {
  return `Namaste! Hamari dukaan ab online hai. Products dekhiye aur order yahin se kijiye: ${storeUrl}`;
}

// City is written to the public store doc and feeds SEO previews, so keep it clean:
// letters (incl. Devanagari), spaces, dots, hyphens, &, apostrophes only; no digits;
// max 40 chars. Mirrors sanitizeCity() in backend/server.js.
function sanitizeCity(raw) {
  return String(raw || "")
    .replace(/[^a-zA-Zऀ-ॿ .&'-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 40)
    .trim() || null;
}

async function trackStoreShare(storeId) {
  if (!storeId) return;
  try {
    await setDoc(doc(db, "stores", storeId), {
      stats: {
        shareCount: increment(1),
        lastSharedAt: serverTimestamp(),
      },
    }, { merge: true });
  } catch (err) {
    console.error("Share tracking failed:", err);
  }
}

async function copyStoreLink(storeId, storeName) {
  const storeUrl = getStoreUrl(storeId);
  await navigator.clipboard?.writeText(getShareText(storeName, storeUrl));
  await trackStoreShare(storeId);
}

async function shareStoreLink(storeId, storeName) {
  const storeUrl = getStoreUrl(storeId);
  if (navigator.share) {
    // Share targets (e.g. WhatsApp) append `url` after `text`, so the text must
    // not contain the URL or the link shows up twice.
    await navigator.share({ title: storeName, text: "Namaste! Hamari dukaan ab online hai. Products dekhiye aur order yahin se kijiye:", url: storeUrl });
    await trackStoreShare(storeId);
    return;
  }
  await copyStoreLink(storeId, storeName);
}

function openWhatsAppShare(storeId, storeName) {
  const storeUrl = getStoreUrl(storeId);
  const text = `Namaste, ab aap ${storeName} se online order kar sakte hain.\n\nMenu dekhein aur order karein:\n${storeUrl}`;
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  trackStoreShare(storeId);
}

// ── New-order alert (chime + tab title + optional browser notification) ──────
let newOrderAudioCtx = null;
function playNewOrderChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!newOrderAudioCtx) newOrderAudioCtx = new Ctx();
    if (newOrderAudioCtx.state === "suspended") newOrderAudioCtx.resume().catch(() => {});
    const now = newOrderAudioCtx.currentTime;
    [880, 1174.66].forEach((freq, i) => {
      const osc = newOrderAudioCtx.createOscillator();
      const gain = newOrderAudioCtx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + i * 0.18);
      gain.gain.exponentialRampToValueAtTime(0.18, now + i * 0.18 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.18 + 0.35);
      osc.connect(gain);
      gain.connect(newOrderAudioCtx.destination);
      osc.start(now + i * 0.18);
      osc.stop(now + i * 0.18 + 0.4);
    });
  } catch {}
}

let titleFlashTimer = null;
function flashNewOrderTitle() {
  try {
    if (titleFlashTimer) return;
    const original = document.title;
    let on = false;
    titleFlashTimer = setInterval(() => {
      on = !on;
      document.title = on ? "🔔 New order!" : original;
    }, 1200);
    const stop = () => {
      if (document.hidden || !titleFlashTimer) return;
      clearInterval(titleFlashTimer);
      titleFlashTimer = null;
      document.title = original;
      document.removeEventListener("visibilitychange", stop);
      window.removeEventListener("focus", stop);
    };
    document.addEventListener("visibilitychange", stop);
    window.addEventListener("focus", stop);
    setTimeout(() => { if (titleFlashTimer) { clearInterval(titleFlashTimer); titleFlashTimer = null; document.title = original; } }, 60000);
  } catch {}
}

function notifyNewOrder(count) {
  playNewOrderChime();
  if (!document.hidden) return;
  flashNewOrderTitle();
  // Never prompts for permission; fires only if the owner already granted it.
  try {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification("KiranaOS", { body: count > 1 ? `${count} new orders received` : "New order received 🛍️" });
    }
  } catch {}
}

function getProductDraft(product) {
  return {
    name: product.name || "",
    category: product.category || CATEGORIES[0],
    unit: product.unit || "",
    price: String(product.price ?? ""),
    stock: String(product.stock ?? ""),
    active: product.active !== false,
    image: product.image || "",
  };
}

function validateProductDraft({ name, unit, price, stock }) {
  const priceNum = Number(price);
  const stockNum = Number(stock);
  if (!name.trim()) return "Product name is required.";
  if (!unit.trim()) return "Unit is required.";
  if (!Number.isFinite(priceNum) || priceNum < 0) return "Price must be zero or more.";
  if (!Number.isInteger(stockNum) || stockNum < 0) return "Stock must be a whole number zero or more.";
  return "";
}

// ─── Global CSS ───────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,700&family=Hind:wght@400;500;700&display=swap');
  * { box-sizing: border-box; }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .dash-fade { animation: fadeUp 0.3s ease forwards; }
  @keyframes pulseOrder {
    0%,100% { opacity: 1; }
    50%      { opacity: 0.75; }
  }
  .order-pulse { animation: pulseOrder 2s ease-in-out infinite; }

  /* ── Mobile responsive ── */
  body { background: #F6F7FB; }
  .dash-tabs-scroll {
    display: flex; gap: 8px; overflow-x: auto; padding: 10px 20px;
    background: rgba(255,255,255,0.78); border-bottom: 1px solid #ECEEF3;
    scrollbar-width: none; white-space: nowrap; backdrop-filter: blur(18px) saturate(160%);
    -webkit-backdrop-filter: blur(18px) saturate(160%);
  }
  .dash-tabs-scroll::-webkit-scrollbar { display: none; }
  .dash-bottom-nav { display: none; }
  .dash-settings-gear { display: none; }
  .premium-surface { position: relative; }
  .premium-surface::before {
    content: ""; position: fixed; inset: 0; pointer-events: none; z-index: 0;
    background:
      radial-gradient(circle at 15% 8%, rgba(232,71,10,0.11), transparent 32%),
      radial-gradient(circle at 78% 0%, rgba(37,99,235,0.08), transparent 28%),
      linear-gradient(180deg, rgba(255,255,255,0.42), rgba(246,247,251,0));
  }
  .premium-surface > * { position: relative; z-index: 1; }
  .metric-card:hover, .mission-card:hover, .order-card-shell:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 42px rgba(16,17,19,0.10);
  }

  @media (max-width: 820px) {
    .stats-row-resp { grid-template-columns: 1fr !important; }
    .order-grid-resp { grid-template-columns: 1fr !important; }
    .inv-table-desktop { display: none !important; }
    .inv-card-list { display: block !important; }
    .dash-body-resp { padding: 14px 12px calc(92px + env(safe-area-inset-bottom, 0px)) !important; }
    .dash-tabs-scroll { display: none !important; }
    .dash-settings-gear { display: inline-flex !important; }
    .dash-bottom-nav {
      display: flex; position: fixed; left: 0; right: 0; bottom: 0; z-index: 300;
      background: rgba(255,255,255,0.94); border-top: 1px solid #ECEEF3;
      padding: 6px 2px calc(8px + env(safe-area-inset-bottom, 0px));
      backdrop-filter: blur(18px) saturate(160%); -webkit-backdrop-filter: blur(18px) saturate(160%);
    }
    .dash-store-meta { display: none !important; }
    .dash-topbar-resp { align-items: flex-start !important; padding: 12px !important; gap: 10px !important; }
    .dash-top-actions { gap: 6px !important; flex-wrap: wrap !important; justify-content: flex-end !important; max-width: 190px !important; }
    .dash-top-actions button { padding: 8px 10px !important; font-size: 12px !important; }
    .dash-live-badge { display: none !important; }
    .help-panel-grid { grid-template-columns: 1fr !important; }
    .help-panel-grid > div:first-child { border-right: 0 !important; border-bottom: 1px solid #ECEEF3 !important; }
    .pulse-grid-resp { grid-template-columns: repeat(2,minmax(0,1fr)) !important; }
    .community-panel-resp { grid-template-columns: 1fr !important; }
    .ds-score-inner { flex-direction: column !important; align-items: flex-start !important; }
    .ds-pulse-row { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 14px !important; padding-top: 16px !important; }
    .ds-grid { grid-template-columns: 1fr !important; }
    .ds-pulse-grid { grid-template-columns: repeat(2, 1fr) !important; }
  }
  @media (min-width: 821px) {
    .inv-card-list { display: none !important; }
  }
  .inv-card-item {
    background: #FFFFFF; border-radius: 16px; padding: 14px; margin-bottom: 10px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06); display: flex; gap: 12px; align-items: flex-start;
  }
  .inv-card-item img { width: 52px; height: 52px; object-fit: contain; border-radius: 8px; background: #F3ECE0; flex-shrink: 0; }
  .inv-card-item-body { flex: 1; min-width: 0; }
  .inv-card-inputs { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 8px; }
  .inv-card-inputs input { width: 100%; padding: 6px 8px; border: 1.5px solid #E8470A; border-radius: 8px; font-size: 13px; outline: none; }
  .inv-card-footer { display: flex; justify-content: space-between; align-items: center; margin-top: 10px; }
`;

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  // Login
  loginPage: {
    minHeight: "100vh", background: "linear-gradient(180deg, #FFF6F0 0%, #FFFDFB 45%, #FFF9F4 100%)",
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", fontFamily: T.fontBody,
    position: "relative", overflow: "hidden", padding: "32px 24px",
  },
  loginBlob: {
    position: "absolute", top: -140, right: -120, width: 320, height: 320,
    borderRadius: "50%", background: "radial-gradient(circle, rgba(255,138,61,0.22) 0%, rgba(255,138,61,0) 70%)", zIndex: 0, pointerEvents: "none",
  },
  loginBlobBottom: {
    position: "absolute", bottom: -160, left: -140, width: 360, height: 360,
    borderRadius: "50%", background: "radial-gradient(circle, rgba(232,71,10,0.10) 0%, rgba(232,71,10,0) 70%)", zIndex: 0, pointerEvents: "none",
  },
  loginContent: { width: "100%", maxWidth: 380, zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center" },
  loginLogo:    { fontFamily: T.fontDisplay, fontSize: 32, fontWeight: 700, color: T.black, marginBottom: 6 },
  loginSub:     { fontFamily: T.fontBody, fontSize: 13, fontWeight: 700, color: T.grey500, marginBottom: 28, letterSpacing: "1.5px", textTransform: "uppercase" },
  loginCard: {
    width: "100%", background: "rgba(255,255,255,0.92)",
    borderRadius: 28, padding: "28px 24px",
    boxShadow: "0 24px 60px rgba(232,71,10,0.10), 0 2px 8px rgba(26,10,0,0.04)",
    border: "1px solid rgba(255,255,255,0.9)",
    backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
    boxSizing: "border-box",
  },
  loginForm:    { width: "100%", display: "flex", flexDirection: "column", gap: 24 },
  floatField:   { position: "relative", width: "100%" },
  floatInput:   {
    width: "100%", border: "none", borderBottom: `1.5px solid ${T.grey300}`,
    outline: "none", padding: "20px 0 8px", fontSize: 16,
    fontFamily: T.fontBody, color: T.black, background: "transparent",
    transition: `border-color 0.2s ${T.ease}`,
  },
  floatLabel: {
    position: "absolute", left: 0, fontSize: 11, fontWeight: 600,
    color: T.grey500, textTransform: "uppercase", letterSpacing: "0.6px",
    fontFamily: T.fontBody, pointerEvents: "none", top: 0,
    transition: `all 0.2s ${T.ease}`,
  },
  showHideBtn: {
    position: "absolute", right: 0, top: 16, background: "none", border: "none",
    cursor: "pointer", fontSize: 12, color: T.grey500, fontFamily: T.fontBody,
    textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600,
  },
  loginBtn:   { width: "100%", height: 52, background: "linear-gradient(135deg, #E8470A 0%, #FF8A3D 100%)", color: T.white, border: "none", borderRadius: T.radiusPill, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: T.fontBody, transition: `opacity 0.2s ${T.ease}`, boxShadow: "0 10px 24px rgba(232,71,10,0.30)" },
  loginError: { color: T.red, fontSize: 13, fontFamily: T.fontBody, textAlign: "center", marginTop: -16 },

  // Dashboard
  dashPage:     { minHeight: "100vh", background: T.offWhite, fontFamily: T.fontBody },
  adminBanner:  { position: "sticky", top: 0, zIndex: 120, background: "linear-gradient(90deg,#101113,#2A2D33)", color: T.white, padding: "9px 20px", fontSize: 12, fontWeight: 800, fontFamily: T.fontBody, textAlign: "center", letterSpacing: "0.4px" },
  dashTopBar:   { background: "rgba(255,255,255,0.86)", borderBottom: `1px solid ${T.grey100}`, padding: "14px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, backdropFilter: "blur(18px) saturate(160%)", WebkitBackdropFilter: "blur(18px) saturate(160%)" },
  dashStoreName:{ fontFamily: T.fontBody, fontSize: 18, fontWeight: 900, color: T.black, margin: 0, letterSpacing: "-0.01em" },
  dashStoreMeta:{ fontSize: 11, color: T.grey500, margin: "3px 0 0", fontFamily: T.fontBody, fontWeight: 700, letterSpacing: "0.2px" },
  dashTopRight: { display: "flex", alignItems: "center", gap: 14 },
  liveBadge:    { display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 800, color: T.green, fontFamily: T.fontBody, background: "#EAFBF3", border: "1px solid #C8F2DD", borderRadius: T.radiusPill, padding: "7px 10px" },
  liveDot:      { width: 7, height: 7, borderRadius: "50%", background: T.green, flexShrink: 0, boxShadow: "0 0 0 4px rgba(14,173,105,0.13)" },
  bellBtn:      { background: "none", border: "none", cursor: "pointer", padding: 4, color: T.grey500, lineHeight: 1 },

  // Tab pills
  dashTabs: { background: T.white, borderBottom: `1px solid ${T.grey100}`, padding: "10px 20px", display: "flex", gap: 8 },
  dashTab: (active) => ({
    padding: "9px 18px", borderRadius: T.radiusPill,
    background: active ? T.black : T.white,
    color: active ? T.white : T.grey600,
    border: `1px solid ${active ? T.black : T.grey100}`,
    boxShadow: active ? "0 10px 24px rgba(16,17,19,0.14)" : "0 1px 2px rgba(16,17,19,0.03)",
    fontSize: 13, fontWeight: active ? 850 : 700,
    cursor: "pointer", fontFamily: T.fontBody, transition: `all 0.2s ${T.ease}`, letterSpacing: "0.01em",
  }),
  dashTabBadge: { background: T.orange, color: T.white, borderRadius: T.radiusPill, fontSize: 10, fontWeight: 700, padding: "1px 7px", marginLeft: 5 },

  // Body
  dashBody: { padding: "24px 20px 120px", maxWidth: 1180, margin: "0 auto" },

  // Stats row
  statsRow:  { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 20 },
  statCard:  { background: "rgba(255,255,255,0.92)", border: `1px solid ${T.grey100}`, borderRadius: T.radiusLg, padding: "16px 16px 18px", boxShadow: T.shadowSm, transition: `all 0.2s ${T.ease}` },
  statLabel: { fontSize: 10, fontWeight: 850, color: T.grey500, textTransform: "uppercase", letterSpacing: "0.85px", margin: "0 0 8px", fontFamily: T.fontBody },
  statValue: { fontSize: 26, fontWeight: 900, color: T.black, margin: 0, fontFamily: T.fontBody, letterSpacing: "-0.03em" },

  // Order grid
  orderGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 14 },

  // Order card
  orderCard: (status) => {
    const borderColor =
      status === "pending"          ? T.orange :
      status === "accepted"         ? T.green  :
      status === "preparing"        ? "#9B1C1C":
      status === "out_for_delivery" ? "#C23608":
      T.grey300;
    return {
      background: "rgba(255,255,255,0.96)", borderRadius: T.radiusLg, padding: "16px 18px",
      boxShadow: T.shadowSm, border: `1px solid ${T.grey100}`, borderTop: `3px solid ${borderColor}`,
      transition: `all 0.2s ${T.ease}`,
    };
  },
  orderCardTop:  { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 },
  customerName:  { fontSize: 15, fontWeight: 700, color: T.black, margin: "0 0 5px", fontFamily: T.fontBody },
  timeAgoText:   { fontSize: 11, color: T.grey300, fontFamily: T.fontBody },
  orderAddress:  { fontSize: 12, color: T.grey500, margin: "0 0 8px", fontFamily: T.fontBody },
  statusPill: (status) => {
    const map = {
      pending:          { bg: T.orangeLight, text: T.orange },
      accepted:         { bg: "#E8F5EE",     text: T.green  },
      preparing:        { bg: "#F7E7E7",     text: "#9B1C1C"},
      out_for_delivery: { bg: "#FBEBE4",     text: "#C23608"},
      delivered:        { bg: T.grey100,     text: T.grey500},
      cancelled:        { bg: "#FDE8E8",     text: T.red    },
    };
    const c = map[status] || { bg: T.grey100, text: T.grey500 };
    return { display: "inline-block", background: c.bg, color: c.text, borderRadius: T.radiusPill, fontSize: 11, fontWeight: 600, padding: "3px 10px", fontFamily: T.fontBody };
  },
  itemsList: { borderTop: `1px solid ${T.grey100}`, paddingTop: 8, marginBottom: 10 },
  itemRow:   { display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 10, alignItems: "start", fontSize: 12, color: T.black, marginBottom: 4, fontFamily: T.fontBody },
  itemName:  { minWidth: 0, overflowWrap: "anywhere", lineHeight: 1.35 },
  itemPrice: { fontWeight: 600, whiteSpace: "nowrap", textAlign: "right" },
  billRow:   { display: "flex", justifyContent: "space-between", fontSize: 12, color: T.grey500, marginTop: 3, fontFamily: T.fontBody },
  billTotal: { display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 700, color: T.black, borderTop: `1px solid ${T.grey100}`, paddingTop: 6, marginTop: 4, fontFamily: T.fontBody },
  actionRow: { display: "flex", gap: 8, marginTop: 10 },
  actionBtn: (variant) => ({
    flex: 1, border: "none", borderRadius: T.radiusPill,
    padding: "10px 12px", fontSize: 12, fontWeight: 850,
    cursor: "pointer", fontFamily: T.fontBody, transition: `all 0.2s ${T.ease}`,
    background:
      variant === "accept"    ? T.black :
      variant === "preparing" ? T.orange :
      variant === "delivery"  ? T.blue :
      T.grey100,
    color:
      variant === "accept"    ? T.white  :
      variant === "preparing" ? T.white :
      variant === "delivery"  ? T.white:
      T.grey500,
  }),
  cancelBtn: { background: "transparent", color: T.red, border: `1px solid ${T.red}`, borderRadius: T.radiusPill, padding: "8px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.fontBody },
  orderId:   { fontSize: 10, color: T.grey300, marginTop: 8, fontFamily: T.fontBody },

  // Status groups
  statusGroup:   { marginBottom: 28 },
  statusHeading: { fontSize: 11, fontWeight: 700, color: T.grey500, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 12, display: "flex", alignItems: "center", gap: 8, fontFamily: T.fontBody },
  statusDot:     (color) => ({ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }),

  // Empty state
  emptyWrap:    { textAlign: "center", padding: "70px 20px", color: T.grey300, fontFamily: T.fontBody },
  emptyIcon:    { fontSize: 48, marginBottom: 12, display: "block" },
  emptyTitle:   { fontSize: 16, fontWeight: 600, color: T.grey500, margin: "0 0 6px", fontFamily: T.fontBody },
  emptySub:     { fontSize: 13, color: T.grey300, fontFamily: T.fontBody },

  // Spinner
  spinner: { width: 28, height: 28, border: `2.5px solid ${T.grey100}`, borderTop: `2.5px solid ${T.orange}`, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "60px auto" },

  // Inventory
  invHeader:   { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 },
  invSearch:   { display: "flex", alignItems: "center", background: T.white, border: `1px solid ${T.grey300}`, borderRadius: T.radiusPill, padding: "8px 16px", gap: 8, flex: 1, maxWidth: 360 },
  invSearchIn: { border: "none", outline: "none", fontSize: 14, flex: 1, color: T.black, background: "transparent", fontFamily: T.fontBody },
  csvBtn:      { background: T.white, color: T.black, border: `1.5px solid ${T.black}`, borderRadius: T.radiusPill, padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: T.fontBody },
  tableWrap:   { background: T.white, borderRadius: T.radiusMd, overflow: "hidden", boxShadow: T.shadowSm },
  tableHead:   { display: "grid", gridTemplateColumns: "36px 48px 1fr 110px 70px 60px 48px 140px", gap: 10, padding: "10px 14px", background: T.offWhite, borderBottom: `1px solid ${T.grey100}`, fontSize: 11, fontWeight: 700, color: T.grey500, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: T.fontBody },
  tableRow:    (dirty) => ({ display: "grid", gridTemplateColumns: "36px 48px 1fr 110px 70px 60px 48px 140px", gap: 10, padding: "10px 14px", borderBottom: `1px solid ${T.grey100}`, alignItems: "center", background: dirty ? "#FFFAF8" : T.white }),
  prodImg:     { width: 40, height: 40, objectFit: "contain", borderRadius: T.radiusSm, background: T.grey100 },
  prodImgFb:   { width: 40, height: 40, borderRadius: T.radiusSm, background: T.grey100, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: T.grey300 },
  prodName:    { fontSize: 13, fontWeight: 600, color: T.black, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: T.fontBody },
  prodCat:     { fontSize: 11, color: T.grey500, fontFamily: T.fontBody },
  inlineInput: { width: "100%", padding: "5px 8px", border: `1.5px solid ${T.orange}`, borderRadius: T.radiusSm, fontSize: 13, outline: "none", fontFamily: T.fontBody, boxSizing: "border-box" },
  toggle:      (on) => ({ width: 36, height: 20, borderRadius: T.radiusPill, background: on ? T.green : T.grey300, position: "relative", cursor: "pointer", flexShrink: 0, transition: `background 0.2s ${T.ease}` }),
  toggleKnob:  (on) => ({ position: "absolute", top: 2, left: on ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: T.white, transition: `left 0.2s ${T.ease}`, boxShadow: T.shadowSm }),
  saveRowBtn:  { background: T.green, color: T.white, border: "none", borderRadius: T.radiusSm, padding: "5px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: T.fontBody },
  invCount:    { fontSize: 13, color: T.grey500, marginBottom: 10, fontFamily: T.fontBody },

  // FAB
  fab: {
    position: "fixed", bottom: 32, right: 28, width: 56, height: 56,
    borderRadius: "50%", background: T.black, color: T.white,
    border: "none", fontSize: 26, cursor: "pointer", boxShadow: T.shadowLg,
    zIndex: 190, display: "flex", alignItems: "center", justifyContent: "center",
    transition: `transform 0.15s ${T.ease}`,
  },

  // Modal
  overlay:      { position: "fixed", inset: 0, background: "rgba(26,10,0,0.4)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, overflowY: "auto" },
  modal:        { background: T.white, borderRadius: T.radiusMd, padding: 24, width: "100%", maxWidth: 480, boxShadow: T.shadowLg, maxHeight: "calc(100dvh - 40px)", overflowY: "auto", margin: "auto" },
  modalTitle:   { fontFamily: T.fontDisplay, fontSize: 20, fontWeight: 700, color: T.black, margin: "0 0 20px" },
  formGroup:    { marginBottom: 14 },
  formLabel:    { display: "block", fontSize: 11, fontWeight: 600, color: T.grey500, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: T.fontBody },
  formInput:    { width: "100%", padding: "10px 12px", border: `1px solid ${T.grey300}`, borderRadius: T.radiusSm, fontSize: 14, outline: "none", fontFamily: T.fontBody, boxSizing: "border-box", color: T.black },
  formSelect:   { width: "100%", padding: "10px 12px", border: `1px solid ${T.grey300}`, borderRadius: T.radiusSm, fontSize: 14, outline: "none", background: T.white, fontFamily: T.fontBody, boxSizing: "border-box", color: T.black },
  modalActions: { display: "flex", gap: 10, marginTop: 20 },
  modalPrimary: { flex: 1, background: T.black, color: T.white, border: "none", borderRadius: T.radiusPill, padding: "12px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: T.fontBody },
  modalCancel:  { background: T.white, color: T.grey500, border: `1px solid ${T.grey300}`, borderRadius: T.radiusPill, padding: "12px 20px", fontSize: 14, cursor: "pointer", fontFamily: T.fontBody },

  // Settings
  settingsSection: { background: T.white, borderRadius: T.radiusMd, padding: 24, marginBottom: 16, boxShadow: T.shadowSm },
  sectionTitle:    { fontSize: 11, fontWeight: 700, color: T.grey500, textTransform: "uppercase", letterSpacing: "0.8px", margin: "0 0 22px", fontFamily: T.fontBody },
  settingLabel:    { display: "block", fontSize: 11, fontWeight: 600, color: T.grey500, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: T.fontBody },
  settingInput:    { width: "100%", padding: "10px 0", border: "none", borderBottom: `1.5px solid ${T.grey300}`, fontSize: 14, outline: "none", fontFamily: T.fontBody, background: "transparent", color: T.black, boxSizing: "border-box", transition: `border-color 0.2s ${T.ease}` },
  saveBtn:         { background: T.black, color: T.white, border: "none", borderRadius: T.radiusPill, padding: "12px 32px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: T.fontBody, marginTop: 8 },

  // QR code section
  qrSection:    { background: T.white, borderRadius: T.radiusMd, padding: 24, marginBottom: 16, boxShadow: T.shadowSm },
  qrWrap:       { display: "flex", flexDirection: "column", alignItems: "center", padding: "20px 0 8px", gap: 14 },
  qrBox:        { padding: 16, border: `1px solid ${T.grey100}`, borderRadius: T.radiusMd, background: T.white, lineHeight: 0 },
  qrCaption:    { fontSize: 13, color: T.grey500, fontFamily: T.fontBody, textAlign: "center" },
  qrDownloadBtn:{ background: T.white, color: T.black, border: `1.5px solid ${T.black}`, borderRadius: T.radiusPill, padding: "9px 22px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: T.fontBody, transition: `all 0.2s ${T.ease}` },

  // Growth dashboard
  heroPanel: { background: `linear-gradient(135deg, ${T.black} 0%, #1B1D23 44%, #2D2448 74%, ${T.orange} 145%)`, borderRadius: 20, padding: 22, color: T.white, boxShadow: T.shadowLg, marginBottom: 16, border: "1px solid rgba(255,255,255,0.12)", overflow: "hidden", position: "relative" },
  heroKicker: { fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.8px", color: "rgba(255,255,255,0.65)", margin: "0 0 8px", fontFamily: T.fontBody },
  heroTitle: { fontFamily: T.fontBody, fontSize: 28, lineHeight: 1.08, fontWeight: 950, margin: "0 0 8px", color: T.white, letterSpacing: "-0.035em" },
  heroSub: { fontSize: 13, color: "rgba(255,255,255,0.72)", margin: 0, lineHeight: 1.5, fontFamily: T.fontBody },
  pulseGrid: { display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 10, marginTop: 18 },
  pulseTile: { background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.16)", borderRadius: T.radiusMd, padding: "12px 13px" },
  pulseValue: { fontSize: 22, fontWeight: 950, margin: "0 0 3px", color: T.white, fontFamily: T.fontBody, letterSpacing: "-0.025em" },
  pulseLabel: { fontSize: 11, fontWeight: 800, margin: 0, color: "rgba(255,255,255,0.62)", fontFamily: T.fontBody, lineHeight: 1.35 },
  kiranNote: { marginTop: 14, background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: T.radiusMd, padding: 13, color: "rgba(255,255,255,0.82)", fontSize: 13, lineHeight: 1.55, fontFamily: T.fontBody },
  focusPanel: { background: "rgba(255,255,255,0.96)", border: `1px solid ${T.grey100}`, borderRadius: 18, padding: 18, boxShadow: T.shadowMd, marginBottom: 14 },
  focusTop: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, flexWrap: "wrap" },
  focusKicker: { fontSize: 11, fontWeight: 900, color: T.orange, textTransform: "uppercase", letterSpacing: "0.8px", margin: "0 0 7px", fontFamily: T.fontBody },
  focusTitle: { fontSize: 21, color: T.black, fontWeight: 950, margin: "0 0 6px", fontFamily: T.fontBody, letterSpacing: "-0.025em", lineHeight: 1.15 },
  focusCopy: { fontSize: 13, color: T.grey600, margin: 0, lineHeight: 1.55, fontFamily: T.fontBody },
  communityPanel: { background: "#F4FBF7", border: "1px solid #D9F2E5", borderRadius: T.radiusLg, padding: 16, marginBottom: 14, display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 14, alignItems: "center" },
  communityTitle: { fontSize: 15, color: T.black, fontWeight: 900, margin: "0 0 4px", fontFamily: T.fontBody },
  communityCopy: { fontSize: 13, color: T.grey600, margin: 0, lineHeight: 1.5, fontFamily: T.fontBody },
  pathPanel: { background: "rgba(255,255,255,0.96)", border: `1px solid ${T.grey100}`, borderRadius: 18, padding: 18, boxShadow: T.shadowSm, marginBottom: 14 },
  pathHeader: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 14 },
  pathTitle: { fontSize: 17, fontWeight: 950, color: T.black, margin: 0, fontFamily: T.fontBody, letterSpacing: "-0.02em" },
  pathSub: { fontSize: 12, color: T.grey500, margin: 0, fontFamily: T.fontBody },
  milestoneTrack: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(132px,1fr))", gap: 10 },
  milestoneCard: (done) => ({ background: done ? T.black : T.grey50, color: done ? T.white : T.black, border: `1px solid ${done ? T.black : T.grey100}`, borderRadius: T.radiusMd, padding: 13, minHeight: 104, boxShadow: done ? "0 14px 28px rgba(16,17,19,0.16)" : "none" }),
  milestoneIcon: (done) => ({ width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: done ? T.orange : T.white, color: done ? T.white : T.orange, fontSize: 13, fontWeight: 950, marginBottom: 10, fontFamily: T.fontBody }),
  milestoneName: { fontSize: 13, fontWeight: 900, margin: "0 0 5px", fontFamily: T.fontBody, lineHeight: 1.25 },
  milestoneMeta: (done) => ({ fontSize: 11, color: done ? "rgba(255,255,255,0.64)" : T.grey500, margin: 0, lineHeight: 1.35, fontFamily: T.fontBody }),
  shareBtnPrimary: { background: T.orange, color: T.white, border: "none", borderRadius: T.radiusPill, padding: "12px 16px", fontSize: 13, fontWeight: 900, cursor: "pointer", fontFamily: T.fontBody, boxShadow: "0 14px 28px rgba(232,71,10,0.30)" },
  shareBtnGhost: { background: "rgba(255,255,255,0.1)", color: T.white, border: "1px solid rgba(255,255,255,0.24)", borderRadius: T.radiusPill, padding: "10px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: T.fontBody },
  progressTrack: { height: 7, borderRadius: T.radiusPill, background: T.grey100, overflow: "hidden" },
  progressFill: (pct) => ({ height: "100%", width: `${Math.max(5, Math.min(100, pct))}%`, borderRadius: T.radiusPill, background: pct >= 100 ? T.green : T.orange }),
  actionPanel: { background: "rgba(255,255,255,0.94)", border: `1px solid ${T.grey100}`, borderRadius: T.radiusLg, padding: 18, boxShadow: T.shadowSm, marginBottom: 14 },
  actionPanelTitle: { fontFamily: T.fontBody, fontSize: 18, color: T.black, fontWeight: 900, margin: "0 0 6px", letterSpacing: "-0.02em" },
  actionPanelSub: { fontSize: 13, color: T.grey500, margin: "0 0 12px", lineHeight: 1.45, fontFamily: T.fontBody },
};

// ─── Dukaan Score styles ──────────────────────────────────────────────────────
// Palette: warm cream / deep ink / muted amber / quiet green — no loud orange/green
const DK = {
  cream:    "#FDFAF7",
  white:    "#FFFFFF",
  border:   "#E8E4DF",
  divider:  "#F2EFEB",
  ink:      "#1A1614",
  inkMid:   "#6B6560",
  inkFaint: "#A09890",
  inkGhost: "#B5AFA8",
  amber:    "#C4A882",  // muted warm progress
  amberBg:  "#FAF5EE",
  green:    "#3A8A5C",  // deep calm completion
  greenBg:  "#EEF7F2",
  alert:    "#B85C2A",  // muted burnt sienna for attention
  alertBg:  "#FDF2EC",
};

const DS = {
  // ── Two-column page grid ──────────────────────────────────────────────────
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 276px",
    gap: 14,
    alignItems: "flex-start",
  },
  col: {},  // plain div wrapper

  // ── Score card ────────────────────────────────────────────────────────────
  scoreCard: {
    background: DK.cream,
    border: `1px solid ${DK.border}`,
    borderRadius: 16,
    padding: "24px 22px 20px",
    marginBottom: 12,
  },
  scoreMeta: {
    fontSize: 10, fontWeight: 700, textTransform: "uppercase",
    letterSpacing: "1.1px", color: DK.inkFaint, fontFamily: T.fontBody,
    margin: "0 0 14px",
  },
  scoreNumRow: {
    display: "flex", alignItems: "baseline", gap: 5, marginBottom: 16,
  },
  scoreNum: {
    fontFamily: T.fontDisplay, fontSize: 58, fontWeight: 600,
    color: DK.ink, lineHeight: 1, letterSpacing: "-0.05em",
  },
  scoreDenom: {
    fontFamily: T.fontBody, fontSize: 17, fontWeight: 400,
    color: DK.inkGhost, lineHeight: 1,
  },
  scoreBarTrack: {
    height: 3, background: DK.divider, borderRadius: 2,
    overflow: "hidden", marginBottom: 14,
  },
  scoreBarFill: (pct, done) => ({
    height: "100%",
    width: `${Math.max(0, Math.min(100, pct))}%`,
    background: done ? DK.green : DK.amber,
    borderRadius: 2,
    transition: "width 0.9s cubic-bezier(0.4,0,0.2,1)",
  }),
  scoreStatus: {
    fontSize: 13, fontWeight: 500, color: DK.inkMid,
    fontFamily: T.fontBody, margin: 0, lineHeight: 1.45,
  },
  scoreStatusDone: {
    fontSize: 13, fontWeight: 600, color: DK.green,
    fontFamily: T.fontBody, margin: 0, lineHeight: 1.45,
  },

  // ── Shared panel ─────────────────────────────────────────────────────────
  panel: {
    background: DK.white,
    border: `1px solid ${DK.border}`,
    borderRadius: 14,
    padding: "16px 18px",
    marginBottom: 12,
  },
  panelHead: {
    display: "flex", alignItems: "baseline",
    justifyContent: "space-between", marginBottom: 14,
  },
  panelTitle: {
    fontSize: 11, fontWeight: 700, textTransform: "uppercase",
    letterSpacing: "0.85px", color: DK.inkFaint,
    fontFamily: T.fontBody, margin: 0,
  },
  panelCount: {
    fontSize: 11, color: DK.inkGhost, fontFamily: T.fontBody, margin: 0,
  },

  // ── Goal rows ─────────────────────────────────────────────────────────────
  goalItem: (done, isLast) => ({
    display: "flex", alignItems: "flex-start", gap: 12,
    padding: "11px 0",
    borderBottom: isLast ? "none" : `1px solid ${DK.divider}`,
  }),
  goalCheckWrap: (done) => ({
    width: 18, height: 18, borderRadius: "50%", flexShrink: 0, marginTop: 2,
    border: `1.5px solid ${done ? DK.green : DK.inkGhost}`,
    background: done ? DK.green : "transparent",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "background 0.2s ease, border-color 0.2s ease",
  }),
  goalBody: { flex: 1, minWidth: 0 },
  goalTitle: (done) => ({
    fontSize: 13, fontWeight: 400,
    color: done ? DK.inkMid : DK.ink,
    fontFamily: T.fontBody, lineHeight: 1.4, display: "block",
  }),
  goalReq: {
    fontSize: 11, color: DK.inkFaint, fontFamily: T.fontBody,
    margin: "2px 0 0", lineHeight: 1.35, display: "block",
  },
  goalCtaRow: {
    display: "flex", gap: 6, marginTop: 7, flexWrap: "wrap",
  },
  goalCta: {
    fontSize: 11, fontWeight: 600, color: DK.ink,
    fontFamily: T.fontBody, background: DK.divider,
    border: `1px solid ${DK.border}`, borderRadius: 6,
    padding: "5px 10px", cursor: "pointer",
    lineHeight: 1, userSelect: "none",
  },
  goalPts: (done) => ({
    fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
    color: done ? DK.green : DK.inkGhost,
    fontFamily: T.fontBody, marginTop: 2, flexShrink: 0,
  }),

  // ── Recommended Next Action ───────────────────────────────────────────────
  nextCard: {
    background: DK.cream,
    border: `1px solid ${DK.border}`,
    borderRadius: 12,
    padding: "14px 16px",
    marginBottom: 12,
    display: "flex", alignItems: "center", gap: 12,
  },
  nextText: {
    flex: 1, fontSize: 13, color: DK.ink,
    fontFamily: T.fontBody, fontWeight: 500, lineHeight: 1.4, margin: 0,
  },
  nextBtn: {
    background: DK.ink, color: DK.cream,
    border: "none", borderRadius: 8,
    padding: "8px 14px", fontSize: 12, fontWeight: 600,
    cursor: "pointer", fontFamily: T.fontBody,
    whiteSpace: "nowrap", flexShrink: 0,
    letterSpacing: "0.01em",
  },
  nextShareRow: {
    display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8,
  },
  nextShareBtn: {
    fontSize: 12, fontWeight: 600, color: DK.ink,
    fontFamily: T.fontBody, background: DK.white,
    border: `1px solid ${DK.border}`, borderRadius: 7,
    padding: "7px 12px", cursor: "pointer",
  },

  // ── Today Pulse (right col) ───────────────────────────────────────────────
  pulseGrid: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0,
  },
  pulseCell: {
    padding: "10px 0 10px 2px",
    borderBottom: `1px solid ${DK.divider}`,
  },
  pulseVal: (alert) => ({
    display: "block", fontSize: 20, fontWeight: 700,
    color: alert ? DK.alert : DK.ink,
    fontFamily: T.fontBody, letterSpacing: "-0.03em", lineHeight: 1,
  }),
  pulseLbl: {
    display: "block", fontSize: 10, color: DK.inkFaint,
    fontFamily: T.fontBody, fontWeight: 600,
    textTransform: "uppercase", letterSpacing: "0.5px",
    marginTop: 3,
  },

  // ── Weekly rhythm ─────────────────────────────────────────────────────────
  weekRow: {
    display: "flex", gap: 4, alignItems: "flex-end", marginTop: 12,
  },
  weekDayWrap: () => ({
    flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
  }),
  weekBarWrap: {
    width: "100%", height: 28,
    display: "flex", flexDirection: "column", justifyContent: "flex-end",
  },
  weekBarFill: (score, isToday) => ({
    width: "100%",
    height: score !== null && score > 0 ? `${Math.max(3, Math.round(score * 0.28))}px` : "2px",
    background: score !== null && score > 0
      ? (isToday ? DK.amber : score >= 100 ? DK.green : "#D5CFC9")
      : DK.divider,
    borderRadius: "2px 2px 0 0",
    transition: "height 0.7s cubic-bezier(0.4,0,0.2,1)",
  }),
  weekScore: (isToday) => ({
    fontSize: 10, fontWeight: isToday ? 700 : 400,
    color: isToday ? DK.ink : DK.inkGhost,
    fontFamily: T.fontBody, lineHeight: 1,
  }),
  weekDayLbl: (isToday) => ({
    fontSize: 8, color: isToday ? DK.inkMid : "#C5BFBA",
    fontFamily: T.fontBody, textTransform: "uppercase",
    letterSpacing: "0.3px", lineHeight: 1,
  }),

  // ── Long-term milestones (quiet list) ────────────────────────────────────
  milestoneItem: (done, isLast) => ({
    display: "flex", alignItems: "center", gap: 10,
    padding: "9px 0",
    borderBottom: isLast ? "none" : `1px solid ${DK.divider}`,
  }),
  milestoneCheck: (done) => ({
    width: 14, height: 14, borderRadius: "50%", flexShrink: 0,
    border: `1.5px solid ${done ? DK.green : DK.border}`,
    background: done ? DK.green : "transparent",
    display: "flex", alignItems: "center", justifyContent: "center",
  }),
  milestoneName: (done) => ({
    flex: 1, fontSize: 12, fontWeight: done ? 500 : 400,
    color: done ? DK.inkMid : DK.ink,
    fontFamily: T.fontBody, lineHeight: 1.3, margin: 0,
  }),
  milestoneProg: {
    fontSize: 10, color: DK.inkGhost, fontFamily: T.fontBody,
    whiteSpace: "nowrap",
  },
  milestoneProgTrack: {
    height: 2, borderRadius: 2, background: DK.divider,
    overflow: "hidden", marginTop: 3, width: "100%",
  },
  milestoneProgFill: (pct) => ({
    height: "100%", width: `${Math.max(4, pct)}%`,
    background: DK.amber, borderRadius: 2,
    transition: "width 0.5s ease",
  }),
  milestoneRow: { flex: 1, minWidth: 0 },

  // ── Share panel ───────────────────────────────────────────────────────────
  sharePanel: {
    background: DK.white,
    border: `1px solid ${DK.border}`,
    borderRadius: 14,
    padding: "16px 18px",
    marginBottom: 12,
  },
  shareQrBox: {
    background: DK.cream, padding: 10,
    borderRadius: 8, border: `1px solid ${DK.border}`,
    lineHeight: 0, display: "inline-block", marginTop: 10, marginBottom: 10,
  },
  shareUrl: {
    fontSize: 11, color: DK.inkFaint, fontFamily: T.fontBody,
    margin: "0 0 10px", wordBreak: "break-all", lineHeight: 1.5,
  },
  shareBtnRow: {
    display: "flex", gap: 6, flexWrap: "wrap",
  },
  shareBtn: (primary) => ({
    fontSize: 12, fontWeight: 600,
    fontFamily: T.fontBody, cursor: "pointer",
    border: `1px solid ${DK.border}`,
    borderRadius: 7, padding: "7px 13px", lineHeight: 1,
    background: primary ? DK.ink : DK.divider,
    color: primary ? DK.cream : DK.ink,
    transition: "opacity 0.15s ease",
  }),
  shareDoneNote: {
    fontSize: 11, color: DK.green, fontFamily: T.fontBody,
    fontWeight: 600, marginTop: 10, display: "flex", alignItems: "center", gap: 5,
  },
};

// ─── FloatField ───────────────────────────────────────────────────────────────
function FloatField({ label, value, onChange, type = "text", showToggle, onToggle, showPass }) {
  const [focused, setFocused] = useState(false);
  const floated = focused || !!value;
  return (
    <div style={S.floatField}>
      <label style={{
        ...S.floatLabel,
        top: floated ? 0 : 20,
        fontSize: floated ? 11 : 15,
        color: focused ? T.black : T.grey500,
      }}>
        {label}
      </label>
      <input
        style={{ ...S.floatInput, borderBottomColor: focused ? T.black : T.grey300, paddingRight: showToggle ? 50 : 0 }}
        type={showToggle ? (showPass ? "text" : "password") : type}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        autoComplete={showToggle ? "current-password" : "off"}
      />
      {showToggle && (
        <button type="button" style={S.showHideBtn} onClick={onToggle}>
          {showPass ? "Hide" : "Show"}
        </button>
      )}
    </div>
  );
}

// ─── LoginScreen ──────────────────────────────────────────────────────────────
const inputStyle = {
  width: "100%", height: 54, borderRadius: 16, border: "1.5px solid #F0E4DC",
  padding: "0 16px", fontSize: 16, fontFamily: "inherit", outline: "none",
  boxSizing: "border-box", background: "#FDF6EC",
};

const SUPPORT_WA = "https://wa.me/918860310303";

function LoginScreen({ onLogin }) {
  const [step,          setStep]         = useState(1);
  const [agreed,        setAgreed]       = useState(false);
  const [phone,         setPhone]        = useState("");
  const [otp,           setOtp]          = useState("");
  const [error,         setError]        = useState("");
  const [unregistered,  setUnregistered] = useState(false);
  const [loading,       setLoading]      = useState(false);
  const [cooldown,      setCooldown]     = useState(0);

  const startCooldown = () => {
    setCooldown(30);
    const t = setInterval(() => {
      setCooldown((c) => { if (c <= 1) { clearInterval(t); return 0; } return c - 1; });
    }, 1000);
  };

  const fullPhone = () => `+91${phone.trim()}`;

  const sendOtp = async () => {
    setError("");
    setUnregistered(false);
    const digits = phone.trim();
    if (!/^[6-9]\d{9}$/.test(digits)) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/otp/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: fullPhone() }),
      });
      if (!res.ok) throw new Error();
      setStep(2);
      startCooldown();
    } catch {
      setError("Failed to send OTP. Try again.");
    }
    setLoading(false);
  };

  const verifyOtp = async () => {
    setError("");
    setUnregistered(false);
    if (otp.length !== 4) { setError("Enter the 4-digit OTP"); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/otp/verify-owner`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: fullPhone(), otp }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.error && data.error.toLowerCase().includes("no owner account")) {
          setUnregistered(true);
        } else {
          throw new Error(data.error || "Invalid OTP");
        }
        setLoading(false);
        return;
      }
      if (data.customToken) {
        await signInWithCustomToken(auth, data.customToken);
      }
      onLogin(data.storeId);
    } catch (err) {
      setError(err.message || "Invalid OTP. Try again.");
    }
    setLoading(false);
  };

  return (
    <div style={S.loginPage}>
      <div style={S.loginBlob} />
      <div style={S.loginBlobBottom} />
      <div style={S.loginContent}>
        <img src="/kiranaos-wordmark.png" alt="KiranaOS" style={{ height: 34, display: "block", marginBottom: 10 }} />
        <p style={S.loginSub}>Store Owner Portal</p>
        {step === 1 ? (
          <div style={{ ...S.loginCard, ...S.loginForm }}>
            <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
              <span style={{
                height: 54, display: "flex", alignItems: "center", padding: "0 12px 0 16px",
                background: "#F5EFE9", border: "1.5px solid #F0E4DC", borderRight: "none",
                borderRadius: "16px 0 0 16px", fontSize: 16, fontWeight: 600, color: "#333", fontFamily: "inherit", whiteSpace: "nowrap", boxSizing: "border-box",
              }}>+91</span>
              <input
                style={{ ...inputStyle, borderRadius: "0 16px 16px 0", flex: 1 }}
                type="tel"
                placeholder="10-digit mobile number"
                value={phone}
                maxLength={10}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
              />
            </div>
            {error && <p style={S.loginError}>{error}</p>}
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 16, cursor: "pointer" }}>
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ marginTop: 2, accentColor: T.orange, width: 16, height: 16, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: T.grey500, fontFamily: T.fontBody, lineHeight: 1.6 }}>
                I agree to the{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: T.orange, textDecoration: "none", fontWeight: 600 }}>Terms of Use</a>
                {" "}and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: T.orange, textDecoration: "none", fontWeight: 600 }}>Privacy Policy</a>
              </span>
            </label>
            <button
              style={{ ...S.loginBtn, opacity: (loading || !agreed) ? 0.6 : 1 }}
              onClick={sendOtp}
              disabled={loading || !agreed}
            >
              {loading ? "Sending…" : "Send OTP"}
            </button>
          </div>
        ) : (
          <div style={{ ...S.loginCard, ...S.loginForm }}>
            <p style={{ textAlign: "center", fontSize: 14, color: "#666", margin: 0 }}>
              OTP sent to <strong>{`+91 ${phone}`}</strong>
            </p>
            <input
              style={{ ...inputStyle, letterSpacing: 8, textAlign: "center", fontSize: 22, fontWeight: 700 }}
              type="text"
              inputMode="numeric"
              placeholder="----"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))}
            />
            {error && <p style={S.loginError}>{error}</p>}
            {unregistered && (
              <p style={{ ...S.loginError, lineHeight: 1.5 }}>
                This number isn't linked to any KiranaOS store. Please contact support on{" "}
                <a href={SUPPORT_WA} target="_blank" rel="noopener noreferrer"
                  style={{ color: "#25D366", fontWeight: 700, textDecoration: "none" }}>
                  WhatsApp
                </a>.
              </p>
            )}
            <button
              style={{ ...S.loginBtn, opacity: loading ? 0.6 : 1 }}
              onClick={verifyOtp}
              disabled={loading}
            >
              {loading ? "Verifying…" : "Verify & Login"}
            </button>
            <p style={{ textAlign: "center", fontSize: 13, color: "#888", margin: 0 }}>
              {cooldown > 0 ? (
                `Resend OTP in ${cooldown}s`
              ) : (
                <span
                  style={{ color: "#E8470A", cursor: "pointer", fontWeight: 600 }}
                  onClick={() => { setError(""); setUnregistered(false); sendOtp(); }}
                >
                  Resend OTP
                </span>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── BrandFooter ─────────────────────────────────────────────────────────────
function BrandFooter() {
  return (
    <a
      href="https://kiranaos.in/how-it-works"
      target="_blank"
      rel="noopener noreferrer"
      style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "34px 0 20px", textDecoration: "none" }}
    >
      <span style={{ fontSize: 10, color: T.grey300, fontFamily: T.fontBody, letterSpacing: "1.2px", textTransform: "uppercase" }}>Powered by</span>
      <img src="/kiranaos-wordmark.png" alt="KiranaOS" style={{ height: 18, display: "block", opacity: 0.85 }} />
    </a>
  );
}

// ─── ShareStoreActions ───────────────────────────────────────────────────────
function ShareStoreActions({ storeId, storeName, compact = false, dark = false }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await copyStoreLink(storeId, storeName);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      alert("Could not copy the link. Please try again.");
    }
  };

  const baseBtn = dark ? S.shareBtnGhost : {
    background: T.white,
    color: T.black,
    border: `1px solid ${T.grey300}`,
    borderRadius: T.radiusPill,
    padding: compact ? "8px 12px" : "10px 14px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: T.fontBody,
  };

  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
      <button
        type="button"
        style={dark ? S.shareBtnPrimary : { ...baseBtn, background: T.orange, color: T.white, border: "none" }}
        onClick={() => openWhatsAppShare(storeId, storeName)}
      >
        WhatsApp
      </button>
      <button
        type="button"
        style={baseBtn}
        onClick={() => shareStoreLink(storeId, storeName).catch(() => {})}
      >
        Share
      </button>
      <button type="button" style={baseBtn} onClick={copy}>
        {copied ? "Copied" : "Copy Link"}
      </button>
    </div>
  );
}

function TopShareButton({ storeId, storeName }) {
  return (
    <button
      type="button"
      style={{
        background: "linear-gradient(135deg,#101113,#2A2D33)", color: T.white, border: "none",
        borderRadius: T.radiusPill, padding: "10px 15px",
        fontSize: 13, fontWeight: 900, cursor: "pointer",
        fontFamily: T.fontBody, whiteSpace: "nowrap",
        boxShadow: "0 12px 24px rgba(16,17,19,0.16)", letterSpacing: "0.01em"
      }}
      onClick={() => shareStoreLink(storeId, storeName).catch(() => {})}
    >
      Share Store
    </button>
  );
}

function HelpButton({ lang, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: T.orangeLight,
        color: T.orange,
        border: `1px solid ${T.orangeLight}`,
        borderRadius: T.radiusPill,
        padding: "10px 13px",
        fontSize: 13,
        fontWeight: 850,
        cursor: "pointer",
        fontFamily: T.fontBody,
        whiteSpace: "nowrap",
        boxShadow: "0 1px 2px rgba(16,17,19,0.04)",
      }}
    >
      {tr(lang, "askKiran")}
    </button>
  );
}

function FloatingTourButton({ lang, onClick }) {
  return ReactDOM.createPortal(
    <button
      type="button"
      onClick={onClick}
      style={{
        position: "fixed",
        right: 18,
        bottom: "calc(88px + env(safe-area-inset-bottom, 0px))",
        zIndex: 350,
        background: T.orange,
        color: T.white,
        border: "none",
        borderRadius: T.radiusPill,
        padding: "12px 18px",
        fontSize: 13,
        fontWeight: 900,
        cursor: "pointer",
        fontFamily: T.fontBody,
        boxShadow: "0 4px 16px rgba(232,71,10,0.35)",
        letterSpacing: "0.01em",
      }}
    >
      {tr(lang, "quickTour")}
    </button>,
    document.body
  );
}

// ─── BottomNav (mobile only, ≤820px — desktop keeps the top tab pills) ───────
const BOTTOM_NAV_ICONS = {
  today: (
    <>
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5.5 10v10h13V10" />
    </>
  ),
  orders: (
    <>
      <path d="M6.2 7.5h11.6l1 12.5H5.2l1-12.5z" />
      <path d="M9 7.5a3 3 0 0 1 6 0" />
    </>
  ),
  inventory: (
    <>
      <path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3z" />
      <path d="m12 12 8-4.5M12 12v9M12 12 4 7.5" />
    </>
  ),
  discounts: (
    <>
      <path d="M11 4H4v7l9.3 9.3a1.7 1.7 0 0 0 2.4 0l4.6-4.6a1.7 1.7 0 0 0 0-2.4L11 4z" />
      <circle cx="8" cy="8" r="1.4" />
    </>
  ),
  analytics: (
    <>
      <path d="M4 20V11M10 20V5M16 20v-6" />
      <path d="M3 20.5h18" />
    </>
  ),
};

function BottomNav({ lang, activeTab, onGo, orderCount }) {
  const items = [
    { id: "today",     label: tr(lang, "tabToday") },
    { id: "orders",    label: tr(lang, "tabOrders"), count: orderCount },
    { id: "inventory", label: tr(lang, "tabInventory") },
    { id: "discounts", label: tr(lang, "tabDiscounts") },
    { id: "analytics", label: tr(lang, "tabAnalytics") },
  ];
  return ReactDOM.createPortal(
    <nav className="dash-bottom-nav">
      {items.map(({ id, label, count }) => {
        const active = activeTab === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onGo(id)}
            aria-current={active ? "page" : undefined}
            style={{
              flex: 1, minWidth: 0, background: "transparent", border: "none", cursor: "pointer",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
              padding: "5px 0 3px", position: "relative",
              color: active ? T.orange : T.grey500, fontFamily: T.fontBody,
            }}
          >
            <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              {BOTTOM_NAV_ICONS[id]}
            </svg>
            {count > 0 && (
              <span style={{ position: "absolute", top: 0, left: "calc(50% + 6px)", background: T.orange, color: T.white, borderRadius: T.radiusPill, fontSize: 10, fontWeight: 700, padding: "1px 6px", lineHeight: 1.5 }}>
                {count > 99 ? "99+" : count}
              </span>
            )}
            <span style={{ fontSize: 11, fontWeight: active ? 800 : 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
              {label}
            </span>
          </button>
        );
      })}
    </nav>,
    document.body
  );
}

// ─── OrderCard ────────────────────────────────────────────────────────────────
function OrderCard({ order, onStatusChange }) {
  const [busy, setBusy] = useState(false);
  const isPending  = order.status === "pending";
  const nextStatus = STATUS_NEXT[order.status];
  const deliveryLat = order.deliveryLocation?.lat;
  const deliveryLng = order.deliveryLocation?.lng;
  const mapsUrl = typeof deliveryLat === "number" && typeof deliveryLng === "number"
    ? `https://www.google.com/maps/search/?api=1&query=${deliveryLat},${deliveryLng}`
    : null;

  const getVariant = (status) => {
    if (status === "pending")    return "accept";
    if (status === "accepted")   return "preparing";
    if (status === "preparing")  return "delivery";
    return "done";
  };

  const go = async (newStatus) => {
    setBusy(true);
    const ok = await onStatusChange(order.id, newStatus);
    setBusy(false);
    // Auto-advance to out_for_delivery 5 minutes after accepting — but ONLY if the
    // accept actually succeeded. A failed accept (e.g. 409 insufficient stock) must
    // never schedule an auto-advance, or the order could later move with no stock deducted.
    if (ok && newStatus === "accepted") {
      setTimeout(() => onStatusChange(order.id, "out_for_delivery"), 5 * 60 * 1000);
    }
  };

  return (
    <div style={S.orderCard(order.status)} className={`order-card-shell ${isPending ? "order-pulse" : ""}`}>
      <div style={S.orderCardTop}>
        <div>
          <p style={S.customerName}>{order.customerName || "Unknown"}</p>
          {order.customerPhone && (
            <p style={{ fontSize: 12, color: T.grey500, margin: "2px 0 0", fontFamily: T.fontBody }}>📞 {order.customerPhone}</p>
          )}
          <span style={S.statusPill(order.status)}>{STATUS_LABEL[order.status]}</span>
        </div>
        <span style={S.timeAgoText}>{timeAgo(order.createdAt)}</span>
      </div>

      {order.address && (
        <p style={S.orderAddress}>📍 {order.address}</p>
      )}
      {mapsUrl && (
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: "inline-flex", margin: "0 0 12px", color: T.orange, fontSize: 12, fontWeight: 700, fontFamily: T.fontBody, textDecoration: "none" }}
        >
          Open in Maps
        </a>
      )}

      <div style={S.itemsList}>
        {(order.items || []).map((item, i) => (
          <div key={i} style={S.itemRow}>
            <span style={S.itemName}>
              {item.name} × {item.qty ?? item.quantity}
              {item.unit && <span style={{ color: T.grey300 }}> ({item.unit})</span>}
            </span>
            <span style={S.itemPrice}>{fmt((item.qty ?? item.quantity) * item.price)}</span>
          </div>
        ))}
        <div style={S.billRow}><span>Subtotal</span><span>{fmt(order.subtotal)}</span></div>
        {order.deliveryCharge > 0
          ? <div style={S.billRow}><span>Delivery</span><span>{fmt(order.deliveryCharge)}</span></div>
          : <div style={{ ...S.billRow, color: T.green }}><span>Delivery</span><span>FREE</span></div>
        }
        <div style={S.billTotal}><span>Total</span><span>{fmt(order.total)}</span></div>
      </div>

      {order.status !== "delivered" && order.status !== "cancelled" && (
        <div style={S.actionRow}>
          {nextStatus && (
            <button
              style={S.actionBtn(getVariant(order.status))}
              type="button"
              disabled={busy}
              onClick={() => go(nextStatus)}
            >
              {busy ? "…" : STATUS_NEXT_LABEL[order.status]}
            </button>
          )}
          {isPending && (
            <button style={S.cancelBtn} type="button" disabled={busy} onClick={() => go("cancelled")}>
              Cancel
            </button>
          )}
        </div>
      )}

      <p style={S.orderId}>#{order.id}</p>
    </div>
  );
}

// ─── ProductRow ───────────────────────────────────────────────────────────────
function ProductRow({ product, onSave, onDelete, storeId, selectedIds, setSelectedIds }) {
  const draft = getProductDraft(product);
  const [price,     setPrice]     = useState(draft.price);
  const [stock,     setStock]     = useState(draft.stock);
  const [active,    setActive]    = useState(draft.active);
  const [editMode,  setEditMode]  = useState(false);
  const [editName,     setEditName]     = useState(draft.name);
  const [editCategory, setEditCategory] = useState(draft.category);
  const [editUnit,     setEditUnit]     = useState(draft.unit);
  const [saving,    setSaving]    = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const [currentImg, setCurrentImg] = useState(draft.image);
  const [error, setError] = useState("");
  const imgInputRef = useRef(null);
  const togglingRef = useRef(false);

  useEffect(() => {
    if (editMode) return;
    if (togglingRef.current) return;
    const next = getProductDraft(product);
    setEditName(next.name);
    setEditCategory(next.category);
    setEditUnit(next.unit);
    setPrice(next.price);
    setStock(next.stock);
    setActive(next.active);
    setCurrentImg(next.image);
    setImgFailed(false);
    setError("");
  }, [editMode, product]);

  const handleImgUpload = async (e) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;
    setImgUploading(true);
    setError("");
    try {
      const { blob, filename } = await prepareImageForUpload(file);
      const fd = new FormData();
      fd.append("image", blob, filename);
      // fetchWithAuthRetry handles stale/expired tokens automatically
      const res = await fetchWithAuthRetry(`${API_BASE}/api/upload-image/${storeId}`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        throw new Error(data.error || "Image upload failed");
      }
      if (data.url) {
        await setDoc(doc(db, "stores", storeId, "products", product.id), { image: data.url }, { merge: true });
        setCurrentImg(data.url);
        setImgFailed(false);
      }
    } catch (err) {
      console.error("Image upload failed:", err);
      setError(err.message || "Image upload failed. Please try again.");
    } finally {
      setImgUploading(false);
      // Reset so re-selecting or re-taking the same photo fires onChange again
      input.value = "";
    }
  };

  const dirty = editMode && (
    editName     !== (product.name || "") ||
    editCategory !== (product.category || CATEGORIES[0]) ||
    editUnit     !== (product.unit || "") ||
    price        !== String(product.price  ?? "") ||
    stock        !== String(product.stock  ?? "") ||
    active       !== (product.active !== false)
  );

  const save = async () => {
    const validationError = validateProductDraft({ name: editName, unit: editUnit, price, stock });
    if (validationError) { setError(validationError); return; }
    setSaving(true);
    setError("");
    try {
      await onSave(product.id, {
        name: editName.trim(),
        category: editCategory,
        unit: editUnit.trim(),
        price: Number(price),
        stock: Number(stock),
        active
      });
      setEditMode(false);
    } catch (err) {
      console.error("Product save failed:", err);
      setError("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    const next = getProductDraft(product);
    setEditName(next.name);
    setEditCategory(next.category);
    setEditUnit(next.unit);
    setPrice(next.price);
    setStock(next.stock);
    setActive(next.active);
    setError("");
    setEditMode(false);
  };

  const handleActiveToggle = async () => {
    if (editMode) {
      setActive(a => !a);
      return;
    }
    const next = !active;
    togglingRef.current = true;
    setActive(next);
    try {
      await onSave(product.id, { active: next });
    } catch (err) {
      console.error("Toggle failed:", err);
      setActive(!next);
      setError("Could not update status. Please try again.");
    } finally {
      togglingRef.current = false;
    }
  };

  return (
    <div style={S.tableRow(dirty)}>
      <div style={{ width: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <input
          type="checkbox"
          checked={selectedIds.includes(product.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedIds(prev => prev.includes(product.id) ? prev : [...prev, product.id]);
            } else {
              setSelectedIds(prev => prev.filter(id => id !== product.id));
            }
          }}
          style={{ width: 16, height: 16, cursor: "pointer" }}
        />
      </div>
      <div style={{ position: "relative", cursor: "pointer", flexShrink: 0 }} onClick={() => imgInputRef.current?.click()} title="Change image">
        {currentImg && !imgFailed
          ? <img style={S.prodImg} src={currentImg} alt={product.name} onError={() => setImgFailed(true)} />
          : <div style={S.prodImgFb}>{(product.name || "?").charAt(0)}</div>
        }
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)", borderRadius: T.radiusSm, display: "flex", alignItems: "center", justifyContent: "center", opacity: imgUploading ? 1 : 0, transition: "opacity 0.2s" }}
          onMouseEnter={(e) => { if (!imgUploading) e.currentTarget.style.opacity = 1; }}
          onMouseLeave={(e) => { if (!imgUploading) e.currentTarget.style.opacity = 0; }}
        >
          {imgUploading
            ? <div style={{ width: 14, height: 14, border: "2px solid white", borderTop: "2px solid transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            : <span style={{ fontSize: 14 }}>📷</span>
          }
        </div>
        <input ref={imgInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImgUpload} />
      </div>
      <div style={{ minWidth: 0 }}>
        {editMode ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <input
              style={{ fontSize: 13, fontWeight: 600, border: "none", borderBottom: `1px solid ${T.grey300}`, padding: "4px 0", fontFamily: T.fontBody, outline: "none", background: "transparent" }}
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Product name"
            />
            <div style={{ display: "flex", gap: 6 }}>
              <select
                style={{ fontSize: 11, border: "none", borderBottom: `1px solid ${T.grey300}`, padding: "2px 0", fontFamily: T.fontBody, outline: "none", flex: 1, color: T.grey600, background: "transparent" }}
                value={editCategory}
                onChange={(e) => setEditCategory(e.target.value)}
              >
                {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
              <input
                style={{ fontSize: 11, border: "none", borderBottom: `1px solid ${T.grey300}`, padding: "2px 0", fontFamily: T.fontBody, outline: "none", width: 60, color: T.grey600, background: "transparent" }}
                type="text"
                value={editUnit}
                onChange={(e) => setEditUnit(e.target.value)}
                placeholder="Unit"
              />
            </div>
          </div>
        ) : (
          <>
            <div style={S.prodName}>{product.name}</div>
            <div style={S.prodCat}>{product.category}{product.unit ? ` · ${product.unit}` : ""}</div>
          </>
        )}
      </div>
      <div style={{ fontSize: 12, color: T.grey500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: T.fontBody }}>{product.category}</div>
      {editMode ? (
        <input style={S.inlineInput} type="number" min="0" step="0.5" value={price} onChange={(e) => setPrice(e.target.value)} />
      ) : (
        <div style={{ fontSize: 13, color: T.black, fontFamily: T.fontBody }}>₹{product.price}</div>
      )}
      {editMode ? (
        <input style={S.inlineInput} type="number" min="0" value={stock} onChange={(e) => setStock(e.target.value)} />
      ) : (
        <div style={{ fontSize: 13, color: T.black, fontFamily: T.fontBody }}>{product.stock}</div>
      )}
      <div style={S.toggle(active)} onClick={handleActiveToggle}>
        <div style={S.toggleKnob(active)} />
      </div>
      {editMode ? (
        <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
          {dirty && (
            <>
              <button
                style={{ fontSize: 11, color: T.white, background: T.orange, border: "none", borderRadius: "4px", padding: "4px 8px", cursor: "pointer", fontFamily: T.fontBody, fontWeight: 600, whiteSpace: "nowrap" }}
                type="button"
                disabled={saving}
                onClick={save}
              >
                {saving ? "..." : "Save"}
              </button>
              <button
                style={{ fontSize: 11, color: T.grey500, background: "transparent", border: `1px solid ${T.grey300}`, borderRadius: "4px", padding: "4px 8px", cursor: "pointer", fontFamily: T.fontBody, fontWeight: 600, whiteSpace: "nowrap" }}
                type="button"
                onClick={cancelEdit}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={() => setEditMode(true)}
            style={{
              fontSize: 11,
              color: T.orange,
              background: "transparent",
              border: `1px solid ${T.orange}`,
              borderRadius: "4px",
              padding: "4px 10px",
              cursor: "pointer",
              fontFamily: T.fontBody,
              fontWeight: 600,
              transition: "all 0.15s ease",
              whiteSpace: "nowrap"
            }}
            onMouseEnter={(e) => {
              e.target.style.background = T.orange;
              e.target.style.color = T.white;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "transparent";
              e.target.style.color = T.orange;
            }}
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => onDelete(product.id)}
            style={{
              fontSize: 11,
              color: T.red,
              background: "transparent",
              border: `1px solid ${T.red}`,
              borderRadius: "4px",
              padding: "4px 10px",
              cursor: "pointer",
              fontFamily: T.fontBody,
              fontWeight: 600,
              transition: "all 0.15s ease",
              whiteSpace: "nowrap"
            }}
            onMouseEnter={(e) => {
              e.target.style.background = T.red;
              e.target.style.color = T.white;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "transparent";
              e.target.style.color = T.red;
            }}
          >
            Delete
          </button>
        </div>
      )}
      {error && (
        <div style={{ gridColumn: "1 / -1", color: T.red, fontSize: 11, fontFamily: T.fontBody }}>
          {error}
        </div>
      )}
    </div>
  );
}

// ─── AddProductModal ──────────────────────────────────────────────────────────
const BLANK_FORM = { name: "", category: CATEGORIES[0], price: "", unit: "", stock: "0", active: true };

function AddProductModal({ onClose, onAdd, lang, storeId }) {
  const [f, setF]   = useState({ ...BLANK_FORM });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const imgInputRef = useRef(null);
  const nameInputRef = useRef(null);
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape" && !saving) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, saving]);

  const doSave = async () => {
    const validationError = validateProductDraft(f);
    if (validationError) { setErr(validationError); return false; }
    setSaving(true);
    setErr(null);
    let imageUrl = "";
    if (imgFile) {
      try {
        const { blob, filename } = await prepareImageForUpload(imgFile);
        const fd = new FormData();
        fd.append("image", blob, filename);
        // fetchWithAuthRetry handles stale/expired tokens automatically
      const res = await fetchWithAuthRetry(`${API_BASE}/api/upload-image/${storeId}`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        throw new Error(data.error || "Image upload failed");
      }
      imageUrl = data.url || "";
    } catch (e) {
      console.error("Add product image upload failed:", e);
      setErr(e.message || "Image upload failed. Please try again.");
      setSaving(false);
      return false;
    }
  }
    try {
      await onAdd({ name: f.name.trim(), category: f.category, price: Number(f.price), unit: f.unit.trim(), stock: Number(f.stock), active: f.active, image: imageUrl });
      return true;
    } catch (e) {
      console.error("Add product failed:", e);
      setErr(tr(lang, "couldNotAdd"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const ok = await doSave();
    if (ok) onClose();
  };

  const handleSaveAndAdd = async () => {
    const ok = await doSave();
    if (ok) {
      setF({ ...BLANK_FORM });
      setImgFile(null);
      setImgPreview(null);
      setTimeout(() => nameInputRef.current?.focus(), 50);
    }
  };

  return ReactDOM.createPortal(
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <p style={S.modalTitle}>{tr(lang, "addItemModalTitle")}</p>

        {/* Required fields */}
        <div style={S.formGroup}>
          <label style={S.formLabel}>{tr(lang, "productName")}</label>
          <input ref={nameInputRef} style={S.formInput} value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Aashirvaad Atta" autoFocus />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={S.formGroup}>
            <label style={S.formLabel}>{tr(lang, "priceLabel")}</label>
            <input style={S.formInput} type="number" min="0" step="0.5" value={f.price} onChange={(e) => set("price", e.target.value)} placeholder="0" />
          </div>
          <div style={S.formGroup}>
            <label style={S.formLabel}>{tr(lang, "unitLabel")}</label>
            <input style={S.formInput} value={f.unit} onChange={(e) => set("unit", e.target.value)} placeholder="e.g. 500g, 1kg" />
          </div>
        </div>

        {/* Optional fields */}
        <div style={S.formGroup}>
          <label style={S.formLabel}>{tr(lang, "categoryLabel")}</label>
          <select style={S.formSelect} value={f.category} onChange={(e) => set("category", e.target.value)}>
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={S.formGroup}>
            <label style={S.formLabel}>{tr(lang, "stockLabel")}</label>
            <input style={S.formInput} type="number" min="0" value={f.stock} onChange={(e) => set("stock", e.target.value)} />
          </div>
          <div style={{ ...S.formGroup, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
            <label style={{ ...S.formLabel, marginBottom: 8 }}>{tr(lang, "availabilityLabel")}</label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontFamily: T.fontBody, fontSize: 14 }}>
              <input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} style={{ width: 18, height: 18, accentColor: T.orange }} />
              {f.active ? (lang === "hi" ? "हाँ" : "Yes") : (lang === "hi" ? "नहीं" : "No")}
            </label>
          </div>
        </div>
        <div style={S.formGroup}>
          <label style={S.formLabel}>{tr(lang, "productImage")}</label>
          <div
            onClick={() => imgInputRef.current?.click()}
            style={{ width: 80, height: 80, borderRadius: 10, border: `2px dashed ${T.grey300}`, background: T.offWhite, cursor: "pointer", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            {imgPreview
              ? <img src={imgPreview} alt="preview" onError={() => setImgPreview(null)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <span style={{ fontSize: 11, color: imgFile ? T.green : T.grey500, fontFamily: T.fontBody, textAlign: "center", padding: 6 }}>{tr(lang, imgFile ? "photoReady" : "tapToAddPhoto")}</span>
            }
          </div>
          <input ref={imgInputRef} type="file" accept="image/*" style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              // Reset so re-selecting or re-taking the same photo fires onChange again
              e.target.value = "";
              if (!file) return;
              setImgFile(file);
              // HEIC/HEIF can't render in <img>; skip the broken object-URL preview.
              // The file still uploads fine (converted to JPEG on save).
              const isHeic = /heic|heif/i.test(file.type) || /\.(heic|heif)$/i.test(file.name || "");
              setImgPreview(isHeic ? null : URL.createObjectURL(file));
            }}
          />
        </div>

        {err && <p style={{ color: T.red, fontSize: 13, marginBottom: 8, fontFamily: T.fontBody }}>{err}</p>}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={S.modalCancel} type="button" onClick={onClose} disabled={saving}>{tr(lang, "cancel")}</button>
          <button
            style={{ ...S.modalPrimary, flex: 1, background: T.white, color: T.orange, border: `1.5px solid ${T.orange}` }}
            type="button" disabled={saving} onClick={handleSaveAndAdd}
          >
            {saving ? "…" : tr(lang, "saveAddAnother")}
          </button>
          <button style={{ ...S.modalPrimary, flex: 1 }} type="button" disabled={saving} onClick={handleSave}>
            {saving ? "…" : tr(lang, "saveItem")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── GoalRow ──────────────────────────────────────────────────────────────────
// Purely presentational — no row-click completion. CTAs are explicit buttons.
function GoalRow({ goal, isLast }) {
  return (
    <div style={DS.goalItem(goal.done, isLast)}>
      <div style={DS.goalCheckWrap(goal.done)}>
        {goal.done && (
          <svg width={9} height={9} viewBox="0 0 9 9" aria-hidden="true">
            <polyline points="1.5,4.5 3.5,6.5 7.5,2" fill="none" stroke="#fff" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <div style={DS.goalBody}>
        <span style={DS.goalTitle(goal.done)}>{goal.label}</span>
        {!goal.done && goal.req && (
          <span style={DS.goalReq}>{goal.req}</span>
        )}
        {!goal.done && goal.cta && (
          <div style={DS.goalCtaRow}>
            <button type="button" style={DS.goalCta} onClick={goal.cta.fn}>
              {goal.cta.label}
            </button>
          </div>
        )}
        {!goal.done && goal.shareCtas && (
          <div style={DS.goalCtaRow}>
            {goal.shareCtas.map((a) => (
              <button key={a.label} type="button" style={DS.goalCta} onClick={a.fn}>
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <span style={DS.goalPts(goal.done)}>+{goal.points}</span>
    </div>
  );
}

// ─── TodayTab — Dukaan Score ──────────────────────────────────────────────────
function TodayTab({ storeId, storeName, onGo }) {
  const [orders,     setOrders]     = useState([]);
  const [products,   setProducts]   = useState([]);
  const [customers,  setCustomers]  = useState([]);
  const [storeStats, setStoreStats] = useState({});
  const [loading,    setLoading]    = useState(true);
  const [manualDone, setManualDone] = useState({});
  const [barScore,   setBarScore]   = useState(0);

  const todayKey    = new Date().toISOString().slice(0, 10);
  const lsGoalsKey  = `kiranaos_dukaan_goals_${storeId}`;
  const lsScoresKey = `kiranaos_dukaan_scores_${storeId}`;

  // Load today's manual goal state from localStorage
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(lsGoalsKey) || "{}");
      if (stored.date === todayKey) setManualDone(stored.goals || {});
    } catch {}
  }, [storeId, lsGoalsKey, todayKey]);

  // Firestore real-time listeners
  const knownOrderIdsRef = useRef(null); // null until first orders snapshot
  useEffect(() => {
    knownOrderIdsRef.current = null;
    const unsubStore = onSnapshot(doc(db, "stores", storeId), (snap) => {
      if (snap.exists()) setStoreStats(snap.data().stats || {});
    });
    const unsubOrders = onSnapshot(
      collection(db, `stores/${storeId}/orders`),
      (snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Alert only on orders that appear after the initial snapshot.
        if (knownOrderIdsRef.current) {
          const fresh = docs.filter((o) => o.status === "pending" && !knownOrderIdsRef.current.has(o.id));
          if (fresh.length > 0) notifyNewOrder(fresh.length);
        }
        knownOrderIdsRef.current = new Set(docs.map((o) => o.id));
        setOrders(docs);
        setLoading(false);
      },
      () => setLoading(false),
    );
    const unsubProducts = onSnapshot(
      collection(db, `stores/${storeId}/products`),
      (snap) => setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
    getDocs(collection(db, `stores/${storeId}/customers`))
      .then((snap) => setCustomers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
    return () => { unsubStore(); unsubOrders(); unsubProducts(); };
  }, [storeId]);

  // Derived metrics
  const todayStart = useMemo(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  }, []);
  const todayOrders = orders.filter((o) => {
    if (!o.createdAt) return false;
    const d = o.createdAt.toDate ? o.createdAt.toDate() : new Date(o.createdAt);
    return d >= todayStart;
  });
  const todayRevenue   = todayOrders.filter((o) => o.status !== "cancelled").reduce((s, o) => s + (Number(o.total) || 0), 0);
  const pendingCount   = orders.filter((o) => o.status === "pending").length;
  const activeProducts = products.filter((p) => p.active !== false);
  const lowStock       = activeProducts.filter((p) => Number(p.stock) > 0 && Number(p.stock) < 5);
  const uniquePhones   = new Set(orders.map((o) => o.customerPhone).filter(Boolean));
  const customerCount  = Math.max(customers.length, uniquePhones.size);
  const shareCount     = Number(storeStats.shareCount || 0);
  const phoneCounts    = {};
  const repeatPhones   = new Set();
  orders.forEach((o) => {
    if (!o.customerPhone) return;
    phoneCounts[o.customerPhone] = (phoneCounts[o.customerPhone] || 0) + 1;
    if (phoneCounts[o.customerPhone] > 1) repeatPhones.add(o.customerPhone);
  });

  // Share goal: only completes when user explicitly clicks a share CTA here
  const markShared = (actionFn) => {
    try { actionFn(); } catch {}
    setManualDone((prev) => {
      const next = { ...prev, shareToday: true };
      try { localStorage.setItem(lsGoalsKey, JSON.stringify({ date: todayKey, goals: next })); } catch {}
      return next;
    });
  };

  // Goals — scoring reworked so default is low, genuine actions earn points
  const shareDone = !!manualDone.shareToday;
  const goals = [
    {
      id: "loggedIn",
      label: "Logged in today",
      points: 5,
      done: true,
      req: null,
      cta: null,
      shareCtas: null,
    },
    {
      id: "storeActive",
      label: "Store is active",
      points: 10,
      done: activeProducts.length > 0,
      req: activeProducts.length === 0 ? "No active products on your shelf" : null,
      cta: activeProducts.length === 0 ? { label: "Open Inventory", fn: () => onGo("inventory") } : null,
      shareCtas: null,
    },
    {
      id: "clearPending",
      label: "No pending orders",
      points: 15,
      done: pendingCount === 0,
      req: pendingCount > 0 ? `${pendingCount} order${pendingCount !== 1 ? "s" : ""} awaiting action` : null,
      cta: pendingCount > 0 ? { label: "Open Orders", fn: () => onGo("orders") } : null,
      shareCtas: null,
    },
    {
      id: "firstOrder",
      label: "First order today",
      points: 15,
      done: todayOrders.length >= 1,
      req: todayOrders.length === 0 ? "No orders received today" : null,
      cta: null,
      shareCtas: null,
    },
    {
      id: "threeOrders",
      label: "3 orders today",
      points: 20,
      done: todayOrders.length >= 3,
      req: todayOrders.length < 3 ? `${todayOrders.length}/3 orders today` : null,
      cta: null,
      shareCtas: null,
    },
    {
      id: "shareToday",
      label: "Share store today",
      points: 15,
      done: shareDone,
      req: !shareDone ? "Send your store link to customers" : null,
      cta: null,
      shareCtas: !shareDone ? [
        { label: "WhatsApp", fn: () => markShared(() => openWhatsAppShare(storeId, storeName)) },
        { label: "Copy link", fn: () => markShared(() => copyStoreLink(storeId, storeName)) },
        { label: "Share", fn: () => markShared(() => shareStoreLink(storeId, storeName).catch(() => {})) },
      ] : null,
    },
    {
      id: "lowStockOk",
      label: "Low stock resolved",
      points: 10,
      done: lowStock.length === 0 && activeProducts.length > 0,
      req: lowStock.length > 0 ? `${lowStock.length} product${lowStock.length !== 1 ? "s" : ""} below restock threshold` : null,
      cta: lowStock.length > 0 ? { label: "Update Stock", fn: () => onGo("inventory") } : null,
      shareCtas: null,
    },
    {
      id: "invFoundation",
      label: "Inventory foundation",
      points: 10,
      done: activeProducts.length >= 25,
      req: activeProducts.length < 25 ? `${activeProducts.length}/25 products live` : null,
      cta: activeProducts.length < 25 ? { label: "Add Products", fn: () => onGo("inventory") } : null,
      shareCtas: null,
    },
  ];

  const score      = Math.min(100, goals.filter((g) => g.done).reduce((s, g) => s + g.points, 0));
  const remaining  = 100 - score;
  const isComplete = score >= 100;

  // Animate progress bar fill after load
  useEffect(() => {
    if (!loading) {
      const t = setTimeout(() => setBarScore(score), 60);
      return () => clearTimeout(t);
    }
  }, [loading, score]);

  // Persist today's score for weekly rhythm
  useEffect(() => {
    if (loading) return;
    try {
      const stored = JSON.parse(localStorage.getItem(lsScoresKey) || "{}");
      stored[todayKey] = score;
      const trimmed = {};
      Object.keys(stored).sort().slice(-14).forEach((k) => { trimmed[k] = stored[k]; });
      localStorage.setItem(lsScoresKey, JSON.stringify(trimmed));
    } catch {}
  }, [score, loading, lsScoresKey, todayKey]);

  // Weekly 7-day strip
  const weekScores = useMemo(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(lsScoresKey) || "{}");
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(); d.setDate(d.getDate() - (6 - i));
        const key = d.toISOString().slice(0, 10);
        return {
          key,
          label: d.toLocaleDateString("en-IN", { weekday: "short" }).slice(0, 2),
          score: key === todayKey ? score : (stored[key] ?? null),
          isToday: key === todayKey,
        };
      });
    } catch { return []; }
  }, [lsScoresKey, todayKey, score]);

  // Long-term milestones
  const milestones = [
    { id: "live",   name: "Dukaan Live",      current: 1,                    target: 1  },
    { id: "shelf",  name: "Shelf Starter",     current: activeProducts.length, target: 25, meta: `${activeProducts.length}/25` },
    { id: "order1", name: "First Order",       current: orders.length,        target: 1  },
    { id: "reach",  name: "Mohalla Reach",      current: shareCount,           target: 25, meta: `${shareCount}/25 shares` },
    { id: "circle", name: "Customer Circle",    current: customerCount,        target: 10, meta: `${customerCount}/10` },
    { id: "repeat", name: "Repeat Buyer",       current: repeatPhones.size,    target: 1  },
  ];

  // Recommended Next Action — single focused priority
  const nextAction = pendingCount > 0
    ? { text: `${pendingCount} order${pendingCount !== 1 ? "s" : ""} need attention.`, btn: "Clear orders", fn: () => onGo("orders"), share: false }
    : lowStock.length > 0
      ? { text: `${lowStock.length} product${lowStock.length !== 1 ? "s" : ""} running low.`, btn: "Update stock", fn: () => onGo("inventory"), share: false }
      : activeProducts.length < 25
        ? { text: `${activeProducts.length}/25 products on your shelf.`, btn: "Add products", fn: () => onGo("inventory"), share: false }
        : !shareDone
          ? { text: "Reach more customers today.", btn: null, fn: null, share: true }
          : null;

  if (loading) return <div style={S.spinner} />;

  return (
    <div className="dash-fade" style={{ maxWidth: 900, margin: "0 auto" }}>
      <div style={DS.grid} className="ds-grid">

        {/* ══ LEFT COLUMN ══════════════════════════════════════════════════ */}
        <div style={DS.col}>

          {/* ── Score card ── */}
          <div style={DS.scoreCard}>
            <p style={DS.scoreMeta}>Dukaan Score · {storeName}</p>
            <div style={DS.scoreNumRow}>
              <span style={DS.scoreNum}>{score}</span>
              <span style={DS.scoreDenom}>/100</span>
            </div>
            <div style={DS.scoreBarTrack}>
              <div style={DS.scoreBarFill(barScore, isComplete)} />
            </div>
            <p style={isComplete ? DS.scoreStatusDone : DS.scoreStatus}>
              {isComplete
                ? "Score complete. Resets tomorrow."
                : score === 0
                  ? "Complete goals below to earn points."
                  : `${remaining} point${remaining !== 1 ? "s" : ""} left today.`}
            </p>
          </div>

          {/* ── Daily Goals ── */}
          <div style={DS.panel}>
            <div style={DS.panelHead}>
              <p style={DS.panelTitle}>Daily Goals</p>
              <p style={DS.panelCount}>{goals.filter((g) => g.done).length}/{goals.length}</p>
            </div>
            {goals.map((goal, i) => (
              <GoalRow key={goal.id} goal={goal} isLast={i === goals.length - 1} />
            ))}
          </div>

          {/* ── Recommended Next Action ── */}
          {nextAction && (
            <div style={DS.nextCard}>
              <p style={DS.nextText}>{nextAction.text}</p>
              {nextAction.btn && (
                <button type="button" style={DS.nextBtn} onClick={nextAction.fn}>
                  {nextAction.btn}
                </button>
              )}
              {nextAction.share && (
                <div style={DS.nextShareRow}>
                  <button type="button" style={DS.nextShareBtn}
                    onClick={() => markShared(() => openWhatsAppShare(storeId, storeName))}>
                    WhatsApp
                  </button>
                  <button type="button" style={DS.nextShareBtn}
                    onClick={() => markShared(() => copyStoreLink(storeId, storeName))}>
                    Copy link
                  </button>
                  <button type="button" style={DS.nextShareBtn}
                    onClick={() => markShared(() => shareStoreLink(storeId, storeName).catch(() => {}))}>
                    Share
                  </button>
                </div>
              )}
            </div>
          )}

        </div>

        {/* ══ RIGHT COLUMN ═════════════════════════════════════════════════ */}
        <div style={DS.col}>

          {/* ── Today Pulse ── */}
          <div style={DS.panel}>
            <p style={{ ...DS.panelTitle, marginBottom: 0 }}>Today</p>
            <div style={DS.pulseGrid} className="ds-pulse-grid">
              <div style={DS.pulseCell}>
                <span style={DS.pulseVal(false)}>{todayOrders.length}</span>
                <span style={DS.pulseLbl}>Orders</span>
              </div>
              <div style={DS.pulseCell}>
                <span style={DS.pulseVal(false)}>{fmt(todayRevenue)}</span>
                <span style={DS.pulseLbl}>Sales</span>
              </div>
              <div style={{ ...DS.pulseCell, borderBottom: "none" }}>
                <span style={DS.pulseVal(pendingCount > 0)}>{pendingCount}</span>
                <span style={DS.pulseLbl}>Pending</span>
              </div>
              <div style={{ ...DS.pulseCell, borderBottom: "none" }}>
                <span style={DS.pulseVal(lowStock.length > 0)}>{lowStock.length}</span>
                <span style={DS.pulseLbl}>Low stock</span>
              </div>
            </div>
          </div>

          {/* ── Running Low list ── */}
          {lowStock.length > 0 && (
            <div style={DS.panel}>
              <div style={DS.panelHead}>
                <p style={DS.panelTitle}>Running low</p>
                <button
                  type="button"
                  style={{ background: "none", border: "none", color: T.orange, fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: T.fontBody, padding: 0 }}
                  onClick={() => onGo("inventory")}
                >
                  Update stock →
                </button>
              </div>
              {lowStock.slice(0, 5).map((p, i) => (
                <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i === Math.min(lowStock.length, 5) - 1 ? "none" : `1px solid ${T.grey100}` }}>
                  <span style={{ fontSize: 13, color: T.black, fontFamily: T.fontBody, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 10 }}>{p.name}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.orange, fontFamily: T.fontBody, flexShrink: 0 }}>{p.stock} left</span>
                </div>
              ))}
              {lowStock.length > 5 && (
                <p style={{ fontSize: 12, color: T.grey500, fontFamily: T.fontBody, margin: "8px 0 0" }}>+{lowStock.length - 5} more in Inventory</p>
              )}
            </div>
          )}

          {/* ── Weekly Rhythm ── */}
          <div style={DS.panel}>
            <p style={DS.panelTitle}>This week</p>
            <div style={DS.weekRow}>
              {weekScores.map((day) => (
                <div key={day.key} style={DS.weekDayWrap(day.isToday)}>
                  <div style={DS.weekBarWrap}>
                    <div style={DS.weekBarFill(day.score, day.isToday)} />
                  </div>
                  <span style={DS.weekScore(day.isToday)}>
                    {day.score !== null ? day.score : "·"}
                  </span>
                  <span style={DS.weekDayLbl(day.isToday)}>{day.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Long-term Progress ── */}
          <div style={DS.panel}>
            <div style={DS.panelHead}>
              <p style={DS.panelTitle}>Milestones</p>
              <p style={DS.panelCount}>
                {milestones.filter((m) => m.current >= m.target).length}/{milestones.length}
              </p>
            </div>
            {milestones.map((m, i) => {
              const done = m.current >= m.target;
              const pct  = Math.min(100, (m.current / m.target) * 100);
              const isLast = i === milestones.length - 1;
              return (
                <div key={m.id} style={DS.milestoneItem(done, isLast)}>
                  <div style={DS.milestoneCheck(done)}>
                    {done && (
                      <svg width={7} height={7} viewBox="0 0 7 7" aria-hidden="true">
                        <polyline points="1,3.5 2.8,5.2 6,1.5" fill="none" stroke="#fff" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <div style={DS.milestoneRow}>
                    <p style={DS.milestoneName(done)}>{m.name}</p>
                    {!done && (
                      <div style={DS.milestoneProgTrack}>
                        <div style={DS.milestoneProgFill(pct)} />
                      </div>
                    )}
                  </div>
                  <span style={DS.milestoneProg}>{done ? "✓" : (m.meta || "")}</span>
                </div>
              );
            })}
          </div>

          {/* ── Share Store ── */}
          <div style={DS.sharePanel}>
            <p style={{ ...DS.panelTitle, marginBottom: 0 }}>Share store</p>
            <div style={DS.shareQrBox}>
              <QRCodeSVG value={getStoreUrl(storeId)} size={72} bgColor={DK.cream} fgColor={DK.ink} level="M" />
            </div>
            <p style={DS.shareUrl}>{getStoreUrl(storeId)}</p>
            {!shareDone ? (
              <div style={DS.shareBtnRow}>
                <button type="button" style={DS.shareBtn(true)}
                  onClick={() => markShared(() => openWhatsAppShare(storeId, storeName))}>
                  WhatsApp
                </button>
                <button type="button" style={DS.shareBtn(false)}
                  onClick={() => markShared(() => shareStoreLink(storeId, storeName).catch(() => {}))}>
                  Share
                </button>
                <button type="button" style={DS.shareBtn(false)}
                  onClick={() => markShared(() => copyStoreLink(storeId, storeName))}>
                  Copy link
                </button>
              </div>
            ) : (
              <div style={DS.shareDoneNote}>
                <svg width={13} height={13} viewBox="0 0 13 13" aria-hidden="true">
                  <circle cx={6.5} cy={6.5} r={6} fill="none" stroke={DK.green} strokeWidth={1.5} />
                  <polyline points="3.5,6.5 5.5,8.5 9.5,4" fill="none" stroke={DK.green} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Shared today
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

// ─── OrdersTab ────────────────────────────────────────────────────────────────
function OrdersTab({ storeId, storeName, onGo }) {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, `stores/${storeId}/orders`),
      (snap) => {
        const data = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const diff = STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status);
            if (diff !== 0) return diff;
            return (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0);
          });
        setOrders(data);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    orders.forEach((o) => { (g[o.status] = g[o.status] || []).push(o); });
    return g;
  }, [orders]);

  const handleStatusChange = async (orderId, newStatus) => {
    // Remember the status before the optimistic flip so we can roll back cleanly
    // if the backend rejects the change (e.g. 409 insufficient stock on accept).
    const prevStatus = orders.find((o) => o.id === orderId)?.status;
    // Optimistic update — flip the card immediately
    setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, status: newStatus } : o));
    try {
      // fetchWithAuthRetry handles expired tokens by forcing a refresh on 401/403
      // so the owner is not logged out mid-session just because the hour rolled over.
      const res  = await fetchWithAuthRetry(`${API_BASE}/api/orders/${storeId}/${orderId}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ status: newStatus }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Roll back optimistic update on failure
        setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, status: prevStatus ?? o.status } : o));
        alert(`Failed to update order: ${data.error || `HTTP ${res.status}`}`);
        return false;
      }
      return true;
    } catch (e) {
      console.error("Status update failed", e);
      // Roll back optimistic update on network error too
      setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, status: prevStatus ?? o.status } : o));
      alert(`Network error: ${e.message}`);
      return false;
    }
  };

  if (loading) return <div style={S.spinner} />;

  // Stats — filter to today's orders only
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayOrders = orders.filter((o) => {
    if (!o.createdAt) return false;
    const d = o.createdAt.toDate ? o.createdAt.toDate() : new Date(o.createdAt);
    return d >= todayStart;
  });
  const totalOrders  = todayOrders.length;
  const pendingCount = (grouped["pending"] || []).length;
  const revenue      = todayOrders
    .filter((o) => o.status !== "cancelled")
    .reduce((s, o) => s + (Number(o.total) || 0), 0);

  const activeStatuses = STATUS_ORDER.filter((s) => s !== "delivered" && s !== "cancelled" && grouped[s]?.length);
  const doneStatuses   = ["delivered", "cancelled"].filter((s) => grouped[s]?.length);
  const statusColors   = { pending: T.orange, accepted: T.green, preparing: "#9B1C1C", out_for_delivery: "#C23608", delivered: T.grey500, cancelled: T.red };

  if (orders.length === 0) {
    return (
      <div style={{ ...S.emptyWrap, background: T.white, borderRadius: T.radiusLg, boxShadow: T.shadowSm }}>
        <span style={S.emptyIcon}>↗</span>
        <p style={{ ...S.emptyTitle, color: T.black }}>No online orders yet</p>
        <p style={{ ...S.emptySub, maxWidth: 360, margin: "0 auto 16px", color: T.grey500 }}>
          Share your store link with customers to get the first order.
        </p>
        <ShareStoreActions storeId={storeId} storeName={storeName} />
        <button
          type="button"
          style={{ ...S.qrDownloadBtn, marginTop: 12 }}
          onClick={() => onGo("today")}
        >
          View Growth Missions
        </button>
      </div>
    );
  }

  return (
    <div className="dash-fade">
      {/* Stat cards */}
      <div style={S.statsRow} className="stats-row-resp">
        <div style={S.statCard}>
          <p style={S.statLabel}>Today's Orders</p>
          <p style={S.statValue}>{totalOrders}</p>
        </div>
        <div style={S.statCard}>
          <p style={S.statLabel}>Revenue</p>
          <p style={{ ...S.statValue, fontSize: 18 }}>{fmt(revenue)}</p>
        </div>
        <div style={S.statCard}>
          <p style={S.statLabel}>Pending</p>
          <p style={{ ...S.statValue, color: pendingCount > 0 ? T.orange : T.black }}>{pendingCount}</p>
        </div>
      </div>

      {activeStatuses.map((status) => (
        <div key={status} style={S.statusGroup}>
          <div style={S.statusHeading}>
            <div style={S.statusDot(statusColors[status])} />
            {STATUS_LABEL[status]} ({grouped[status].length})
          </div>
          <div style={S.orderGrid} className="order-grid-resp">
            {grouped[status].map((o) => <OrderCard key={o.id} order={o} onStatusChange={handleStatusChange} />)}
          </div>
        </div>
      ))}

      {doneStatuses.length > 0 && (
        <details style={{ marginTop: 16 }}>
          <summary style={{ cursor: "pointer", fontSize: 13, color: T.grey500, fontWeight: 600, marginBottom: 12, userSelect: "none", fontFamily: T.fontBody }}>
            Completed / Cancelled ({doneStatuses.reduce((s, st) => s + grouped[st].length, 0)})
          </summary>
          {doneStatuses.map((status) => (
            <div key={status} style={S.statusGroup}>
              <div style={S.statusHeading}>
                <div style={S.statusDot(statusColors[status])} />
                {STATUS_LABEL[status]} ({grouped[status].length})
              </div>
              <div style={S.orderGrid} className="order-grid-resp">
                {grouped[status].map((o) => <OrderCard key={o.id} order={o} onStatusChange={handleStatusChange} />)}
              </div>
            </div>
          ))}
        </details>
      )}
    </div>
  );
}

// ─── ProductCardMobile ─────────────────────────────────────────────────────────
function ProductCardMobile({ product, onSave, onDelete, storeId, selectedIds, setSelectedIds }) {
  const draft = getProductDraft(product);
  const [price,  setPrice]  = useState(draft.price);
  const [stock,  setStock]  = useState(draft.stock);
  const [active, setActive] = useState(draft.active);
  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState(draft.name);
  const [editCategory, setEditCategory] = useState(draft.category);
  const [editUnit, setEditUnit] = useState(draft.unit);
  const [saving, setSaving] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const [currentImg, setCurrentImg] = useState(draft.image);
  const [error, setError] = useState("");
  const imgInputRef = useRef(null);
  const togglingRef = useRef(false);

  useEffect(() => {
    if (editMode) return;
    if (togglingRef.current) return;
    const next = getProductDraft(product);
    setEditName(next.name);
    setEditCategory(next.category);
    setEditUnit(next.unit);
    setPrice(next.price);
    setStock(next.stock);
    setActive(next.active);
    setCurrentImg(next.image);
    setError("");
  }, [editMode, product]);

  const handleImgUpload = async (e) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;
    setImgUploading(true);
    setError("");
    try {
      const { blob, filename } = await prepareImageForUpload(file);
      const fd = new FormData();
      fd.append("image", blob, filename);
      // fetchWithAuthRetry handles stale/expired tokens automatically
      const res = await fetchWithAuthRetry(`${API_BASE}/api/upload-image/${storeId}`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        throw new Error(data.error || "Image upload failed");
      }
      if (data.url) {
        await setDoc(doc(db, "stores", storeId, "products", product.id), { image: data.url }, { merge: true });
        setCurrentImg(data.url);
      }
    } catch (err) {
      console.error("Image upload failed:", err);
      setError(err.message || "Image upload failed. Please try again.");
    } finally {
      setImgUploading(false);
      // Reset so re-selecting or re-taking the same photo fires onChange again
      input.value = "";
    }
  };

  const dirty = editMode && (
    editName     !== (product.name || "") ||
    editCategory !== (product.category || CATEGORIES[0]) ||
    editUnit     !== (product.unit || "") ||
    price        !== String(product.price  ?? "") ||
    stock        !== String(product.stock  ?? "") ||
    active       !== (product.active !== false)
  );

  const save = async () => {
    const validationError = validateProductDraft({ name: editName, unit: editUnit, price, stock });
    if (validationError) { setError(validationError); return; }
    setSaving(true);
    setError("");
    try {
      await onSave(product.id, {
        name: editName.trim(),
        category: editCategory,
        unit: editUnit.trim(),
        price: Number(price),
        stock: Number(stock),
        active
      });
      setEditMode(false);
    } catch (err) {
      console.error("Product save failed:", err);
      setError("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    const next = getProductDraft(product);
    setEditName(next.name);
    setEditCategory(next.category);
    setEditUnit(next.unit);
    setPrice(next.price);
    setStock(next.stock);
    setActive(next.active);
    setError("");
    setEditMode(false);
  };

  const handleActiveToggle = async () => {
    if (editMode) {
      setActive(a => !a);
      return;
    }
    const next = !active;
    togglingRef.current = true;
    setActive(next);
    try {
      await onSave(product.id, { active: next });
    } catch (err) {
      console.error("Toggle failed:", err);
      setActive(!next);
      setError("Could not update status. Please try again.");
    } finally {
      togglingRef.current = false;
    }
  };

  return (
    <div className="inv-card-item">
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
        <input
          type="checkbox"
          checked={selectedIds.includes(product.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedIds(prev => prev.includes(product.id) ? prev : [...prev, product.id]);
            } else {
              setSelectedIds(prev => prev.filter(id => id !== product.id));
            }
          }}
          style={{ width: 18, height: 18, cursor: "pointer", marginTop: 4 }}
        />
      </div>
      <div style={{ position: "relative", cursor: "pointer", flexShrink: 0 }} onClick={() => imgInputRef.current?.click()}>
        {currentImg
          ? <img src={currentImg} alt={product.name} style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 8 }} />
          : <div style={{ width: 52, height: 52, borderRadius: 8, background: T.grey100, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: T.grey300 }}>{(product.name || "?").charAt(0)}</div>
        }
        <div style={{ position: "absolute", bottom: 0, right: 0, background: T.orange, borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10 }}>
          {imgUploading ? "…" : "📷"}
        </div>
        <input ref={imgInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImgUpload} />
      </div>
      <div className="inv-card-item-body">
        {editMode ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
            <input
              style={{ fontSize: 14, fontWeight: 600, border: "none", borderBottom: `1px solid ${T.grey300}`, padding: "6px 0", fontFamily: T.fontBody, outline: "none", background: "transparent", width: "100%" }}
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Product name"
            />
            <div style={{ display: "flex", gap: 8 }}>
              <select
                style={{ fontSize: 12, border: "none", borderBottom: `1px solid ${T.grey300}`, padding: "4px 0", fontFamily: T.fontBody, outline: "none", flex: 1, color: T.grey600, background: "transparent" }}
                value={editCategory}
                onChange={(e) => setEditCategory(e.target.value)}
              >
                {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
              <input
                style={{ fontSize: 12, border: "none", borderBottom: `1px solid ${T.grey300}`, padding: "4px 0", fontFamily: T.fontBody, outline: "none", width: 80, color: T.grey600, background: "transparent" }}
                type="text"
                value={editUnit}
                onChange={(e) => setEditUnit(e.target.value)}
                placeholder="Unit"
              />
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.black, fontFamily: T.fontBody, marginBottom: 2 }}>{product.name}</div>
            <div style={{ fontSize: 12, color: T.grey500, fontFamily: T.fontBody }}>{product.category}{product.unit ? ` · ${product.unit}` : ""}</div>
          </>
        )}
        <div className="inv-card-inputs">
          <div>
            <div style={{ fontSize: 10, color: T.grey500, fontWeight: 600, marginBottom: 3, fontFamily: T.fontBody }}>PRICE ₹</div>
            {editMode ? (
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 12, color: T.grey500, fontFamily: T.fontBody }}>₹</span>
                <input style={{ ...S.inlineInput, width: 70 }} type="number" min="0" step="0.5" value={price} onChange={(e) => setPrice(e.target.value)} />
              </div>
            ) : (
              <div style={{ fontSize: 15, fontWeight: 700, color: T.black, fontFamily: T.fontBody }}>₹{product.price}</div>
            )}
          </div>
          <div>
            <div style={{ fontSize: 10, color: T.grey500, fontWeight: 600, marginBottom: 3, fontFamily: T.fontBody }}>STOCK</div>
            {editMode ? (
              <input style={{ ...S.inlineInput, width: 50 }} type="number" min="0" value={stock} onChange={(e) => setStock(e.target.value)} />
            ) : (
              <div style={{ fontSize: 13, color: T.grey600, fontFamily: T.fontBody }}>Stock: {product.stock}</div>
            )}
          </div>
        </div>
        <div className="inv-card-footer">
          <div style={S.toggle(active)} onClick={handleActiveToggle}>
            <div style={S.toggleKnob(active)} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
          {editMode ? (
            <>
              {dirty && (
                <>
                  <button
                    style={{ flex: 1, fontSize: 13, color: T.white, background: T.orange, border: "none", borderRadius: "6px", padding: "8px", cursor: "pointer", fontFamily: T.fontBody, fontWeight: 600 }}
                    type="button"
                    disabled={saving}
                    onClick={save}
                  >
                    {saving ? "..." : "Save"}
                  </button>
                  <button
                    style={{ flex: 1, fontSize: 13, color: T.grey500, background: "transparent", border: `1px solid ${T.grey300}`, borderRadius: "6px", padding: "8px", cursor: "pointer", fontFamily: T.fontBody, fontWeight: 600 }}
                    type="button"
                    onClick={cancelEdit}
                  >
                    Cancel
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setEditMode(true)}
                style={{ flex: 1, fontSize: 13, color: T.orange, background: "transparent", border: `1px solid ${T.orange}`, borderRadius: "6px", padding: "8px", cursor: "pointer", fontFamily: T.fontBody, fontWeight: 600 }}
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => onDelete(product.id)}
                style={{ flex: 1, fontSize: 13, color: T.red, background: "transparent", border: `1px solid ${T.red}`, borderRadius: "6px", padding: "8px", cursor: "pointer", fontFamily: T.fontBody, fontWeight: 600 }}
              >
                Delete
              </button>
            </>
          )}
        </div>
        {error && (
          <div style={{ color: T.red, fontSize: 12, fontFamily: T.fontBody, marginTop: 8 }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CsvPreviewModal ───────────────────────────────────────────────────────────
function CsvPreviewModal({ rows, onConfirm, onClose, saving }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape" && !saving) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, saving]);

  return ReactDOM.createPortal(
    <div style={S.overlay} onClick={onClose}>
      <div style={{ ...S.modal, maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <p style={S.modalTitle}>CSV Preview ({rows.length} products)</p>
        <div style={{ maxHeight: 320, overflowY: "auto", marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 70px 70px", gap: 6, padding: "6px 0", borderBottom: `1px solid ${T.grey100}`, fontSize: 11, fontWeight: 700, color: T.grey500, textTransform: "uppercase", fontFamily: T.fontBody }}>
            <div>Name</div><div>Category</div><div>Price</div><div>Stock</div>
          </div>
          {rows.map((r, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 100px 70px 70px", gap: 6, padding: "5px 0", borderBottom: `1px solid ${T.grey100}`, fontSize: 13, fontFamily: T.fontBody }}>
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</div>
              <div style={{ color: T.grey500 }}>{r.category}</div>
              <div>₹{r.price}</div>
              <div>{r.stock}</div>
            </div>
          ))}
        </div>
        {rows.length === 0 && <p style={{ color: T.red, fontSize: 13, fontFamily: T.fontBody }}>No valid rows found. Check column names: name, category, price, unit, stock</p>}
        <div style={S.modalActions}>
          <button style={S.modalCancel} type="button" onClick={onClose}>Cancel</button>
          {rows.length > 0 && (
            <button style={S.modalPrimary} type="button" disabled={saving} onClick={onConfirm}>{saving ? "Uploading…" : `Upload ${rows.length} Products`}</button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Incremental list rendering ───────────────────────────────────────────────
// Stores with a large catalogue (demo-store has 500 products) used to mount
// every row at once, which makes the first paint and every keystroke in the
// search box slow on the low-end Android phones merchants actually use.
// Rows are stateful and variable-height, so instead of fixed-height windowing
// we render a page at a time and extend as the merchant scrolls.
// Selection and bulk actions still operate on the full filtered set.
const INVENTORY_PAGE_SIZE = 40;

function useIncrementalList(items, pageSize = INVENTORY_PAGE_SIZE) {
  const [count, setCount] = useState(pageSize);

  // Any change to the list (search, delete, reload) restarts from the first page.
  useEffect(() => { setCount(pageSize); }, [items, pageSize]);

  const visible = useMemo(() => items.slice(0, count), [items, count]);
  const hasMore = count < items.length;
  const showMore = () => setCount((c) => Math.min(c + pageSize, items.length));
  return { visible, hasMore, showMore, total: items.length };
}

// Sentinel that pulls in the next page when it scrolls into view. Also renders
// a button so the list stays usable without IntersectionObserver.
function LoadMoreSentinel({ onVisible, remaining }) {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) onVisible(); },
      { rootMargin: "600px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [onVisible]);

  return (
    <div ref={ref} style={{ padding: 16, textAlign: "center" }}>
      <button
        type="button"
        onClick={onVisible}
        style={{
          border: `1px solid ${T.grey100}`, background: T.white, color: T.grey500,
          borderRadius: 100, padding: "10px 18px", fontSize: 13, fontWeight: 600,
          fontFamily: T.fontBody, cursor: "pointer",
        }}
      >
        Show more ({remaining} left)
      </button>
    </div>
  );
}

// ─── InventoryTab ─────────────────────────────────────────────────────────────
function InventoryTab({ storeId, lang }) {
  const [products,   setProducts]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [showAdd,    setShowAdd]    = useState(false);
  const [csvRows,    setCsvRows]    = useState(null);
  const [csvSaving,  setCsvSaving]  = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [inventoryError, setInventoryError] = useState("");
  const csvInputRef  = useRef(null);
  const [storePaused, setStorePaused] = useState(false);
  const [pauseMsg,    setPauseMsg]    = useState("");

  const downloadTemplate = () => {
    const content = [
      "name,category,price,unit,stock",
      "Parle-G Biscuits,Biscuits & Snacks,10,250g,50",
      "Tata Salt,Staples,20,1kg,30",
      "Amul Butter,Dairy & Bakery,55,100g,20",
    ].join("\n");
    const blob = new Blob([content], { type: "text/csv" });
    const a    = document.createElement("a");
    a.href     = URL.createObjectURL(blob);
    a.download = "kiranaos-product-template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  useEffect(() => {
    Promise.all([
      getDocs(collection(db, "stores", storeId, "products")),
      getDoc(doc(db, `stores/${storeId}`)),
    ])
      .then(([snap, storeSnap]) => {
        const data = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.category || "").localeCompare(b.category || "") || (a.name || "").localeCompare(b.name || ""));
        setProducts(data);
        setStorePaused(storeSnap.exists() ? !!storeSnap.data().storePaused : false);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Inventory load failed:", err);
        setInventoryError("Could not load inventory. Please refresh.");
        setLoading(false);
      });
  }, [storeId]);

  const handleSave = async (id, updates) => {
    await updateDoc(doc(db, "stores", storeId, "products", id), updates);
    setProducts((prev) => prev.map((p) => p.id === id ? { ...p, ...updates } : p));
  };

  const handleAdd = async (newProduct) => {
    const ref = await addDoc(collection(db, "stores", storeId, "products"), { ...newProduct, createdAt: serverTimestamp() });
    setProducts((prev) => [...prev, { id: ref.id, ...newProduct }]);
    setSelectedIds([]);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product? This cannot be undone.")) return;
    setInventoryError("");
    try {
      await deleteDoc(doc(db, "stores", storeId, "products", id));
      setProducts((prev) => prev.filter((p) => p.id !== id));
      setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
    } catch (err) {
      console.error("Product delete failed:", err);
      setInventoryError("Delete failed. Please try again.");
    }
  };

  const handleCsvFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setInventoryError("");
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const { headers, rows: parsed } = parseCsv(ev.target.result);
        const hasNameColumn = ["name", "product name", "product_name", "item", "item name", "item_name"].some((h) => headers.includes(h));
        if (!hasNameColumn) {
          setInventoryError("CSV needs a product name column. Use 'name' or 'product_name', then try again.");
          setCsvRows([]);
          return;
        }
        const rows = parsed.map((row) => {
          const name = csvValue(row, ["name", "product name", "product_name", "item", "item name", "item_name"]);
          const category = csvValue(row, ["category", "product category", "product_category"]) || CATEGORIES[0];
          const price = parseCsvNumber(csvValue(row, ["price", "price_inr", "selling price", "selling_price", "mrp"]));
          const unit = csvValue(row, ["unit", "pack_size", "pack size", "size", "weight"]) || "1 pc";
          const stock = Math.max(0, Math.floor(parseCsvNumber(csvValue(row, ["stock", "qty", "quantity"]), 20)));
          const image = csvValue(row, ["image", "image_url", "image url", "photo", "photo_url"])
            || "https://storage.googleapis.com/kiranaos-product-images/placeholder.svg";
          return { name, category, price, unit, stock, active: true, image };
        }).filter((r) => r.name);
        if (!rows.length) {
          setInventoryError("No valid products found in CSV. Check columns: name, category, price, unit, stock.");
        }
        setCsvRows(rows);
      } catch (err) {
        console.error("CSV parse failed:", err);
        setInventoryError("Could not read this CSV. Please check the file format and try again.");
        setCsvRows([]);
      }
    };
    reader.onerror = () => {
      setInventoryError("Could not read this CSV file. Please try again.");
      setCsvRows([]);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleCsvConfirm = async () => {
    if (!csvRows?.length) return;
    setCsvSaving(true);
    try {
      const BATCH_SIZE = 450;
      for (let i = 0; i < csvRows.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        csvRows.slice(i, i + BATCH_SIZE).forEach((row) => {
          const ref = doc(collection(db, "stores", storeId, "products"));
          batch.set(ref, { ...row, createdAt: serverTimestamp() });
        });
        await batch.commit();
      }
      const snap = await getDocs(collection(db, "stores", storeId, "products"));
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.category || "").localeCompare(b.category || "") || (a.name || "").localeCompare(b.name || ""));
      setProducts(data);
      setCsvRows(null);
      setSelectedIds([]);
    } catch (err) {
      console.error("CSV upload failed:", err);
      setInventoryError(err.message || "CSV upload failed. Please try again.");
    }
    setCsvSaving(false);
  };

  const handlePauseToggle = async () => {
    const next = !storePaused;
    setInventoryError("");
    setStorePaused(next);
    try {
      const batch = writeBatch(db);
      products.forEach((p) => batch.update(doc(db, "stores", storeId, "products", p.id), { active: !next }));
      await batch.commit();
      await setDoc(doc(db, "stores", storeId), { storePaused: next }, { merge: true });
      setProducts((prev) => prev.map((p) => ({ ...p, active: !next })));
      setPauseMsg(next ? "All products turned off" : "All products are live");
      setTimeout(() => setPauseMsg(""), 3000);
    } catch (err) {
      console.error("Pause store failed:", err);
      setStorePaused(!next);
      setInventoryError("Pause store failed. Please try again.");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => `${p.name} ${p.category} ${p.unit}`.toLowerCase().includes(q));
  }, [products, search]);
  // Render a page at a time; small catalogues (most stores) fit in the first
  // page, so this is a no-op for them.
  const { visible: visibleProducts, hasMore, showMore, total } = useIncrementalList(filtered);
  const filteredIds = useMemo(() => filtered.map((p) => p.id), [filtered]);
  const selectedFilteredIds = useMemo(() => selectedIds.filter((id) => filteredIds.includes(id)), [filteredIds, selectedIds]);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.includes(id));

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => filteredIds.includes(id)));
  }, [filteredIds]);

  const handleSelectAllFiltered = (checked) => {
    setSelectedIds(checked ? filteredIds : []);
  };

  const handleBulkDelete = async () => {
    if (!selectedFilteredIds.length) return;
    if (!window.confirm(`Delete ${selectedFilteredIds.length} products? This cannot be undone.`)) return;
    setBulkDeleting(true);
    setInventoryError("");
    try {
      const batch = writeBatch(db);
      selectedFilteredIds.forEach(id => batch.delete(doc(db, "stores", storeId, "products", id)));
      await batch.commit();
      setProducts(prev => prev.filter(p => !selectedFilteredIds.includes(p.id)));
      setSelectedIds([]);
    } catch (err) {
      console.error("Bulk delete failed:", err);
      setInventoryError("Bulk delete failed. Please try again.");
    } finally {
      setBulkDeleting(false);
    }
  };

  if (loading) return <div style={S.spinner} />;

  return (
    <div className="dash-fade">
      {/* Pause Store toggle */}
      <div style={{ ...S.settingsSection, marginBottom: 14, borderLeft: `4px solid ${storePaused ? T.red : T.orange}` }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: T.black, fontFamily: T.fontBody }}>Pause Store</div>
            <div style={{ fontSize: 12, color: T.grey500, fontFamily: T.fontBody, marginTop: 2 }}>Turn off all products at once</div>
          </div>
          <div style={S.toggle(!storePaused)} onClick={handlePauseToggle}>
            <div style={S.toggleKnob(!storePaused)} />
          </div>
        </div>
        {pauseMsg && <div style={{ fontSize: 12, color: T.grey500, fontFamily: T.fontBody, marginTop: 8 }}>{pauseMsg}</div>}
      </div>

      {/* Search bar */}
      <div style={{ ...S.invSearch, marginBottom: 10, maxWidth: "100%" }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.grey500} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          style={S.invSearchIn}
          type="text"
          placeholder="Search products…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <span style={{ fontSize: 14, color: T.grey500, cursor: "pointer" }} onClick={() => setSearch("")}>✕</span>
        )}
      </div>

      {/* Bulk select / delete bar */}
      {filtered.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, padding: "10px 12px", background: T.grey50, borderRadius: T.radiusMd }}>
          <input
            type="checkbox"
            checked={allFilteredSelected}
            onChange={(e) => handleSelectAllFiltered(e.target.checked)}
            style={{ width: 16, height: 16, cursor: "pointer" }}
            aria-label="Select all visible products"
          />
          <span style={{ fontSize: 13, color: T.grey600, fontFamily: T.fontBody }}>
            Select All ({selectedFilteredIds.length} selected)
          </span>
          {selectedFilteredIds.length > 0 && (
            <button
              type="button"
              disabled={bulkDeleting}
              onClick={handleBulkDelete}
              style={{ marginLeft: "auto", background: T.red, color: T.white, border: "none", borderRadius: T.radiusPill, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.fontBody }}
            >
              {bulkDeleting ? "Deleting..." : `Delete Selected (${selectedFilteredIds.length})`}
            </button>
          )}
        </div>
      )}
      {inventoryError && (
        <div style={{ color: T.red, fontSize: 12, fontFamily: T.fontBody, margin: "0 0 12px" }}>
          {inventoryError}
        </div>
      )}

      {/* Primary Add Item button */}
      <button
        style={{ width: "100%", background: T.orange, color: T.white, border: "none", borderRadius: T.radiusMd, padding: "13px", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: T.fontBody, marginBottom: 16, letterSpacing: "0.2px" }}
        type="button"
        onClick={() => setShowAdd(true)}
      >
        + {tr(lang, "addItem")}
      </button>

      {/* Secondary: Bulk upload */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <button
          style={{ background: "none", color: T.grey500, border: "none", padding: 0, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.fontBody, whiteSpace: "nowrap", textDecoration: "underline" }}
          type="button"
          onClick={() => csvInputRef.current?.click()}
        >
          {tr(lang, "bulkUpload")} (CSV)
        </button>
        <span style={{ color: T.grey300, fontSize: 12 }}>·</span>
        <button
          style={{ background: "none", color: T.grey500, border: "none", padding: 0, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.fontBody, whiteSpace: "nowrap", textDecoration: "underline" }}
          type="button"
          onClick={downloadTemplate}
        >
          {tr(lang, "downloadTemplate")}
        </button>
        <input ref={csvInputRef} type="file" accept=".csv" style={{ display: "none" }} onChange={handleCsvFile} />
      </div>

      <p style={S.invCount}>
        {filtered.length} product{filtered.length !== 1 ? "s" : ""}{search ? ` matching "${search}"` : ""}
      </p>

      {/* Desktop table */}
      <div style={S.tableWrap} className="inv-table-desktop">
        <div style={S.tableHead}>
          <div style={{ width: 40 }} />
          <div />
          <div>Product</div>
          <div>Category</div>
          <div>Price ₹</div>
          <div>Stock</div>
          <div>On</div>
          <div />
        </div>
        {visibleProducts.map((p) => <ProductRow key={p.id} product={p} onSave={handleSave} onDelete={handleDelete} storeId={storeId} selectedIds={selectedIds} setSelectedIds={setSelectedIds} />)}
        {hasMore && <LoadMoreSentinel onVisible={showMore} remaining={total - visibleProducts.length} />}
        {filtered.length === 0 && (
          <div style={{ padding: 28, textAlign: "center", color: T.grey300, fontSize: 13, fontFamily: T.fontBody }}>No products found</div>
        )}
      </div>

      {/* Mobile card list */}
      <div className="inv-card-list">
        {visibleProducts.map((p) => <ProductCardMobile key={p.id} product={p} onSave={handleSave} onDelete={handleDelete} storeId={storeId} selectedIds={selectedIds} setSelectedIds={setSelectedIds} />)}
        {hasMore && <LoadMoreSentinel onVisible={showMore} remaining={total - visibleProducts.length} />}
        {filtered.length === 0 && (
          <div style={{ padding: 28, textAlign: "center", color: T.grey300, fontSize: 13, fontFamily: T.fontBody }}>No products found</div>
        )}
      </div>

      {showAdd && <AddProductModal onClose={() => setShowAdd(false)} onAdd={handleAdd} lang={lang} storeId={storeId} />}
      {csvRows  && <CsvPreviewModal rows={csvRows} saving={csvSaving} onConfirm={handleCsvConfirm} onClose={() => setCsvRows(null)} />}
    </div>
  );
}

// ─── AnalyticsTab ─────────────────────────────────────────────────────────────
function AnalyticsTab({ storeId }) {
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, `stores/${storeId}/orders`),
      (snap) => { setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoading(false); },
      () => setLoading(false)
    );
    return () => unsub();
  }, []);

  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);

  const todayOrders = orders.filter((o) => {
    if (!o.createdAt) return false;
    const d = o.createdAt.toDate ? o.createdAt.toDate() : new Date(o.createdAt);
    return d >= todayStart;
  });
  const todayRevenue = todayOrders.filter((o) => o.status !== "cancelled").reduce((s, o) => s + (Number(o.total) || 0), 0);
  const todayCount   = todayOrders.length;
  const avgOrderValue = todayCount > 0 ? todayRevenue / todayCount : 0;

  // Scan all order items for product and category tallies
  const productCounts  = {};
  const categoryCounts = {};
  orders.forEach((o) => {
    (o.items || []).forEach((item) => {
      const qty = item.quantity || item.qty || 1;
      const key = item.name || item.id || "Unknown";
      productCounts[key]  = (productCounts[key]  || 0) + qty;
      const cat = item.category || "Unknown";
      categoryCounts[cat] = (categoryCounts[cat] || 0) + qty;
    });
  });
  const top5       = Object.entries(productCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const topCatEntry = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1])[0];
  const topCategory = topCatEntry ? topCatEntry[0] : "—";

  // Last 7 days bar chart data
  const last7 = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i); d.setHours(0, 0, 0, 0);
    const end = new Date(d); end.setDate(end.getDate() + 1);
    const rev = orders
      .filter((o) => {
        if (!o.createdAt || o.status === "cancelled") return false;
        const t = o.createdAt.toDate ? o.createdAt.toDate() : new Date(o.createdAt);
        return t >= d && t < end;
      })
      .reduce((s, o) => s + (Number(o.total) || 0), 0);
    last7.push({ date: d, revenue: rev, isToday: i === 0 });
  }
  const maxRev = Math.max(...last7.map((d) => d.revenue), 1);

  if (loading) return <div style={S.spinner} />;

  return (
    <div className="dash-fade">
      {/* Summary stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 20 }} className="stats-row-resp">
        <div style={S.statCard}>
          <p style={S.statLabel}>Today's Revenue</p>
          <p style={{ ...S.statValue, fontSize: 18 }}>{fmt(todayRevenue)}</p>
        </div>
        <div style={S.statCard}>
          <p style={S.statLabel}>Orders Today</p>
          <p style={S.statValue}>{todayCount}</p>
        </div>
        <div style={S.statCard}>
          <p style={S.statLabel}>Avg Order Value</p>
          <p style={{ ...S.statValue, fontSize: 18 }}>{fmt(avgOrderValue)}</p>
        </div>
      </div>

      {/* 7-day bar chart */}
      <div style={{ ...S.settingsSection, marginBottom: 16 }}>
        <p style={S.sectionTitle}>Revenue — Last 7 Days</p>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 110, padding: "0 4px" }}>
          {last7.map(({ date, revenue, isToday }, i) => {
            const pct   = (revenue / maxRev) * 90;
            const label = date.toLocaleDateString("en-IN", { weekday: "short" });
            return (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, height: "100%", justifyContent: "flex-end" }}>
                {revenue > 0 && <span style={{ fontSize: 9, color: T.grey500, fontFamily: T.fontBody }}>₹{Math.round(revenue)}</span>}
                <div style={{ width: "100%", height: `${Math.max(pct, 4)}%`, background: isToday ? T.orange : T.grey300, borderRadius: "4px 4px 0 0", transition: "height 0.5s ease", minHeight: 4 }} />
                <span style={{ fontSize: 9, color: isToday ? T.orange : T.grey500, fontFamily: T.fontBody, fontWeight: isToday ? 700 : 400 }}>{label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top 5 products */}
      <div style={{ ...S.settingsSection, marginBottom: 16 }}>
        <p style={S.sectionTitle}>Top 5 Products Ordered</p>
        {top5.length === 0 ? (
          <p style={{ fontSize: 13, color: T.grey300, fontFamily: T.fontBody }}>No order data yet</p>
        ) : top5.map(([name, count], i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${T.grey100}`, fontFamily: T.fontBody }}>
            <span style={{ fontSize: 13, color: T.black }}>{i + 1}. {name}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: T.orange, background: T.orangeLight, borderRadius: T.radiusPill, padding: "2px 10px" }}>{count} ordered</span>
          </div>
        ))}
      </div>

      {/* Most popular category */}
      <div style={S.settingsSection}>
        <p style={S.sectionTitle}>Most Popular Category</p>
        {topCatEntry
          ? <p style={{ ...S.statValue, fontSize: 22, fontFamily: T.fontDisplay, margin: 0 }}>{topCategory} <span style={{ fontSize: 14, color: T.grey500, fontFamily: T.fontBody, fontWeight: 400 }}>({topCatEntry[1]} items)</span></p>
          : <p style={{ fontSize: 13, color: T.grey300, fontFamily: T.fontBody }}>No data yet</p>
        }
      </div>
    </div>
  );
}

// ─── DiscountsTab ─────────────────────────────────────────────────────────────
function DiscountsTab({ storeId }) {
  const [freeDeliveryOn,     setFreeDeliveryOn]     = useState(false);
  const [freeDeliveryAmount, setFreeDeliveryAmount] = useState("199");
  const [deliveryChargeAmount, setDeliveryChargeAmount] = useState("10");
  const [savingDelivery,     setSavingDelivery]     = useState(false);
  const [deliverySaved,      setDeliverySaved]      = useState(false);
  const [discountSearch,     setDiscountSearch]     = useState("");
  const [discountProducts,   setDiscountProducts]   = useState([]);
  const [allProducts,        setAllProducts]        = useState([]);
  const [selectedProduct,    setSelectedProduct]    = useState(null);
  const [discountPct,        setDiscountPct]        = useState("");
  const [savingDiscount,     setSavingDiscount]     = useState(false);
  const [discountSaved,      setDiscountSaved]      = useState(false);
  const [loading,            setLoading]            = useState(true);

  useEffect(() => {
    Promise.all([
      getDoc(doc(db, "stores", storeId)),
      getDocs(collection(db, "stores", storeId, "products")),
    ]).then(([storeSnap, productsSnap]) => {
      if (storeSnap.exists()) {
        const d = storeSnap.data();
        setFreeDeliveryOn(!!d.freeDeliveryEnabled);
        setFreeDeliveryAmount(String(d.freeDeliveryAmount ?? 199));
        setDeliveryChargeAmount(String(d.deliveryChargeAmount ?? 10));
      }
      const prods = productsSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setAllProducts(prods);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [storeId]);

  useEffect(() => {
    const q = discountSearch.trim().toLowerCase();
    if (!q) { setDiscountProducts([]); return; }
    setDiscountProducts(allProducts.filter((p) => `${p.name} ${p.category}`.toLowerCase().includes(q)).slice(0, 8));
  }, [discountSearch, allProducts]);

  const saveDelivery = async () => {
    setSavingDelivery(true); setDeliverySaved(false);
    const parsedDeliveryCharge = Number(deliveryChargeAmount);
    const parsedFreeDeliveryAmount = Number(freeDeliveryAmount);
    try {
      await setDoc(doc(db, "stores", storeId), {
        freeDeliveryEnabled: freeDeliveryOn,
        deliveryChargeAmount: Number.isFinite(parsedDeliveryCharge) && parsedDeliveryCharge >= 0 ? parsedDeliveryCharge : 10,
        freeDeliveryAmount:  Number.isFinite(parsedFreeDeliveryAmount) && parsedFreeDeliveryAmount >= 0 ? parsedFreeDeliveryAmount : 199,
      }, { merge: true });
      setDeliverySaved(true);
      setTimeout(() => setDeliverySaved(false), 3000);
    } catch (err) { console.error("Save delivery failed:", err); }
    setSavingDelivery(false);
  };

  const saveProductDiscount = async () => {
    if (!selectedProduct) return;
    setSavingDiscount(true); setDiscountSaved(false);
    try {
      const pct = parseFloat(discountPct) || 0;
      await updateDoc(doc(db, `stores/${storeId}/products/${selectedProduct.id}`), { discount: pct });
      setAllProducts((prev) => prev.map((p) => p.id === selectedProduct.id ? { ...p, discount: pct } : p));
      setDiscountSaved(true);
      setTimeout(() => { setDiscountSaved(false); setSelectedProduct(null); setDiscountPct(""); setDiscountSearch(""); }, 2000);
    } catch (err) { console.error("Save discount failed:", err); }
    setSavingDiscount(false);
  };

  if (loading) return <div style={S.spinner} />;

  return (
    <div style={{ maxWidth: 560 }} className="dash-fade">
      {/* Delivery fees */}
      <div style={S.settingsSection}>
        <p style={S.sectionTitle}>Delivery Fees</p>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, fontFamily: T.fontBody, color: T.black }}>
            Delivery charge ₹
          </span>
          <input
            style={{ ...S.settingInput, width: 90, display: "inline-block" }}
            type="number"
            min="0"
            step="1"
            value={deliveryChargeAmount}
            onChange={(e) => setDeliveryChargeAmount(e.target.value)}
          />
          <span style={{ fontSize: 13, fontFamily: T.fontBody, color: T.grey500 }}>
            per order
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16, flexWrap: "wrap" }}>
          <div style={S.toggle(freeDeliveryOn)} onClick={() => setFreeDeliveryOn((v) => !v)}>
            <div style={S.toggleKnob(freeDeliveryOn)} />
          </div>
          <span style={{ fontSize: 14, fontFamily: T.fontBody, color: T.black }}>
            Free delivery on orders above ₹
          </span>
          <input
            style={{ ...S.settingInput, width: 80, display: "inline-block" }}
            type="number"
            min="0"
            step="1"
            value={freeDeliveryAmount}
            onChange={(e) => setFreeDeliveryAmount(e.target.value)}
            disabled={!freeDeliveryOn}
          />
        </div>
        {deliverySaved && <p style={{ color: T.green, fontSize: 13, fontFamily: T.fontBody, marginBottom: 8 }}>✓ Saved</p>}
        <button style={S.saveBtn} type="button" disabled={savingDelivery} onClick={saveDelivery}>
          {savingDelivery ? "Saving…" : "Save"}
        </button>
      </div>

      {/* Product Discount */}
      <div style={S.settingsSection}>
        <p style={S.sectionTitle}>Product Discounts</p>
        <p style={{ fontSize: 13, color: T.grey500, fontFamily: T.fontBody, marginBottom: 14 }}>
          Search a product and set a discount percentage. Discounted price shows on the customer menu.
        </p>
        {allProducts.filter((p) => p.discount > 0).length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: T.grey500, fontFamily: T.fontBody, letterSpacing: "0.6px", marginBottom: 8 }}>ACTIVE DISCOUNTS</p>
            {allProducts.filter((p) => p.discount > 0).map((p) => (
              <div key={p.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: T.offWhite, borderRadius: T.radiusSm, marginBottom: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600, fontFamily: T.fontBody, color: T.black, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</p>
                  <p style={{ margin: "2px 0 0", fontSize: 12, fontFamily: T.fontBody, color: T.grey500 }}>
                    <span style={{ textDecoration: "line-through", marginRight: 6 }}>₹{Number(p.price).toFixed(0)}</span>
                    <span style={{ color: T.green, fontWeight: 600 }}>₹{(p.price * (1 - p.discount / 100)).toFixed(0)}</span>
                  </p>
                </div>
                <span style={{ marginLeft: 10, padding: "2px 8px", background: "#E6F9F0", color: T.green, fontSize: 12, fontWeight: 700, borderRadius: T.radiusPill, whiteSpace: "nowrap" }}>{p.discount}% off</span>
                <button
                  type="button"
                  style={{ marginLeft: 10, padding: "4px 10px", fontSize: 12, fontFamily: T.fontBody, background: "transparent", border: `1px solid ${T.grey300}`, borderRadius: T.radiusPill, color: T.grey500, cursor: "pointer", whiteSpace: "nowrap" }}
                  onClick={async () => {
                    try {
                      await updateDoc(doc(db, "stores", storeId, "products", p.id), { discount: 0 });
                      setAllProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, discount: 0 } : x));
                    } catch (err) { console.error("Remove discount failed:", err); }
                  }}
                >Remove</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ ...S.invSearch, marginBottom: 10 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.grey500} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            style={S.invSearchIn}
            type="text"
            placeholder="Search product…"
            value={discountSearch}
            onChange={(e) => setDiscountSearch(e.target.value)}
          />
        </div>
        {discountProducts.map((p) => (
          <div
            key={p.id}
            style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 10px", borderRadius: T.radiusSm, cursor: "pointer", background: selectedProduct?.id === p.id ? T.orangeLight : T.grey100, marginBottom: 6 }}
            onClick={() => { setSelectedProduct(p); setDiscountPct(String(p.discount || "")); }}
          >
            <span style={{ flex: 1, fontSize: 13, fontWeight: 500, fontFamily: T.fontBody }}>{p.name}</span>
            <span style={{ fontSize: 12, color: T.grey500, fontFamily: T.fontBody }}>₹{p.price}</span>
            {p.discount > 0 && <span style={{ fontSize: 11, background: T.green, color: T.white, borderRadius: 4, padding: "1px 6px", fontFamily: T.fontBody }}>{p.discount}% off</span>}
          </div>
        ))}
        {selectedProduct && (
          <div style={{ background: T.offWhite, borderRadius: T.radiusSm, padding: "12px 14px", marginTop: 10 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: T.black, fontFamily: T.fontBody, margin: "0 0 10px" }}>
              Set discount for: <strong>{selectedProduct.name}</strong>
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input
                style={{ ...S.formInput, width: 100 }}
                type="number"
                min="0"
                max="99"
                placeholder="0"
                value={discountPct}
                onChange={(e) => setDiscountPct(e.target.value)}
              />
              <span style={{ fontSize: 14, fontFamily: T.fontBody }}>% off</span>
              {discountPct && <span style={{ fontSize: 13, color: T.green, fontFamily: T.fontBody }}>= ₹{(selectedProduct.price * (1 - parseFloat(discountPct) / 100)).toFixed(2).replace(/\.00$/, "")}</span>}
            </div>
            {discountSaved && <p style={{ color: T.green, fontSize: 13, fontFamily: T.fontBody, marginTop: 8 }}>✓ Discount saved</p>}
            <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
              <button style={{ ...S.saveBtn, marginTop: 0, padding: "10px 24px" }} type="button" disabled={savingDiscount} onClick={saveProductDiscount}>
                {savingDiscount ? "Saving…" : "Apply Discount"}
              </button>
              <button style={{ ...S.modalCancel, padding: "10px 16px" }} type="button" onClick={() => { setSelectedProduct(null); setDiscountPct(""); }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SettingsTab ──────────────────────────────────────────────────────────────
// ─── LanguagePickerModal ──────────────────────────────────────────────────────
// Shown once when a merchant has no saved language preference.
// Not dismissible without a choice — no close button by design.
function LanguagePickerModal({ onSelect }) {
  const [busy, setBusy] = useState(false);
  const choose = (code) => {
    if (busy) return;
    setBusy(true);
    onSelect(code);
  };
  return ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(26,10,0,0.55)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: T.white, borderRadius: T.radiusLg, padding: "40px 32px", maxWidth: 400, width: "100%", textAlign: "center", boxShadow: T.shadowLg }} className="dash-fade">
        <p style={{ fontSize: 22, fontWeight: 700, fontFamily: T.fontBody, color: T.black, margin: "0 0 6px", lineHeight: 1.3 }}>
          Which language are you comfortable with?
        </p>
        <p style={{ fontSize: 14, color: T.grey500, fontFamily: T.fontBody, margin: "0 0 32px" }}>
          आप किस भाषा में काम करना पसंद करते हैं?
        </p>
        <div style={{ display: "flex", gap: 16 }}>
          {[{ code: "en", label: "English" }, { code: "hi", label: "हिन्दी" }].map(({ code, label }) => (
            <button
              key={code}
              type="button"
              disabled={busy}
              onClick={() => choose(code)}
              style={{ flex: 1, padding: "18px 12px", borderRadius: T.radiusMd, border: `2px solid ${T.orange}`, background: T.white, color: T.orange, fontSize: 18, fontWeight: 700, fontFamily: T.fontBody, cursor: busy ? "not-allowed" : "pointer" }}
            >
              {label}
            </button>
          ))}
        </div>
        <p style={{ fontSize: 12, color: T.grey500, fontFamily: T.fontBody, margin: "20px 0 0" }}>
          You can change this later in Settings.
        </p>
      </div>
    </div>,
    document.body
  );
}

const HELP_TOPICS = [
  {
    id: "add-product",
    question: "Product kaise add karun?",
    hi: "Inventory tab kholiye, Add Item dabaiye, product ka naam, price, unit aur stock bhariye. Phir Save dabaiye. Product turant customer menu mein dikhne lagega.",
    en: "Open the Inventory tab, tap Add Item, enter the product name, price, unit, and stock. Then tap Save. The product will appear in the customer menu.",
  },
  {
    id: "edit-price-stock",
    question: "Price/stock kaise badlun?",
    hi: "Inventory tab mein product ke paas Edit dabaiye. Price ya stock badaliye, phir Save dabaiye. Customer menu mein naya price/stock update ho jayega.",
    en: "In the Inventory tab, tap Edit next to the product. Change the price or stock, then tap Save. The customer menu will use the updated details.",
  },
  {
    id: "turn-off-product",
    question: "Product off kaise karun?",
    hi: "Agar product available nahi hai, Inventory mein us product ka On/Off toggle band kar dijiye. Product delete nahi hoga, bas customer ko nahi dikhega.",
    en: "If a product is unavailable, turn off its On/Off toggle in Inventory. The product is not deleted; it simply stops showing to customers.",
  },
  {
    id: "how-it-works",
    question: "KiranaOS kaise kaam karta hai?",
    hi: "Customer QR scan karta hai ya store link kholta hai, products dekhta hai aur order place karta hai. Aapko order dashboard mein dikhta hai. Phir aap accept, pack aur deliver kar sakte hain.",
    en: "Customers scan your QR or open your store link, browse products, and place an order. You see the order in the dashboard, then accept, pack, and deliver it.",
  },
  {
    id: "more-customers",
    question: "Zyada customers kaise laun?",
    hi: "Apna store link regular customers, WhatsApp groups aur society groups mein bhejiye. Counter par QR code lagaiye. Har order ke baad customer ko link save karne ko boliye.",
    en: "Share your store link with regular customers, WhatsApp groups, and society groups. Put the QR code near your counter. After each order, ask customers to save the link.",
  },
  {
    id: "qr-code",
    question: "QR code kahan milega?",
    hi: "Settings kholiye — mobile par upar right mein gear (⚙) icon, desktop par Settings tab. Wahan Store QR Code section milega. QR download karke print kar sakte hain ya customers ko share kar sakte hain.",
    en: "Open Settings — the gear (⚙) icon at the top right on mobile, or the Settings tab on desktop. Find the Store QR Code section there. You can download the QR, print it, or share it with customers.",
  },
];

function HelpPanel({ lang, onClose }) {
  const [selectedId, setSelectedId] = useState(HELP_TOPICS[0].id);
  const selected = HELP_TOPICS.find((topic) => topic.id === selectedId) || HELP_TOPICS[0];

  return ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(26,10,0,0.38)", zIndex: 540, display: "flex", alignItems: "flex-end", justifyContent: "center", padding: 16 }}>
      <div style={{ background: T.white, borderRadius: T.radiusLg, width: "100%", maxWidth: 680, maxHeight: "86vh", overflow: "hidden", boxShadow: T.shadowLg }} className="dash-fade">
        <div style={{ padding: "18px 18px 14px", borderBottom: `1px solid ${T.grey100}`, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div>
            <p style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 900, color: T.black, fontFamily: T.fontBody }}>{tr(lang, "helpTitle")}</p>
            <p style={{ margin: 0, fontSize: 13, color: T.grey500, fontFamily: T.fontBody, lineHeight: 1.45 }}>{tr(lang, "helpSubtitle")}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: T.white, border: `1px solid ${T.grey200}`, borderRadius: T.radiusPill, padding: "8px 12px", fontSize: 12, fontWeight: 800, color: T.grey600, cursor: "pointer", fontFamily: T.fontBody }}
          >
            {tr(lang, "helpClose")}
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(180px,0.7fr) minmax(0,1.3fr)", gap: 0, maxHeight: "calc(86vh - 82px)", overflow: "auto" }} className="help-panel-grid">
          <div style={{ padding: 14, borderRight: `1px solid ${T.grey100}`, display: "grid", gap: 8, alignContent: "start" }}>
            {HELP_TOPICS.map((topic) => (
              <button
                key={topic.id}
                type="button"
                onClick={() => setSelectedId(topic.id)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: selected.id === topic.id ? T.black : T.grey50,
                  color: selected.id === topic.id ? T.white : T.black,
                  border: `1px solid ${selected.id === topic.id ? T.black : T.grey100}`,
                  borderRadius: T.radiusSm,
                  padding: "11px 12px",
                  fontSize: 13,
                  fontWeight: 800,
                  lineHeight: 1.35,
                  cursor: "pointer",
                  fontFamily: T.fontBody,
                }}
              >
                {topic.question}
              </button>
            ))}
          </div>
          <div style={{ padding: 18, display: "grid", gap: 14, alignContent: "start" }}>
            <p style={{ margin: 0, fontSize: 17, fontWeight: 900, color: T.black, fontFamily: T.fontBody, lineHeight: 1.3 }}>{selected.question}</p>
            <div style={{ background: T.orangeLight, borderRadius: T.radiusMd, padding: 14 }}>
              <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 900, color: T.orange, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: T.fontBody }}>Hindi</p>
              <p style={{ margin: 0, fontSize: 14, color: T.black, lineHeight: 1.65, fontFamily: T.fontBody }}>{selected.hi}</p>
            </div>
            <div style={{ background: T.grey50, border: `1px solid ${T.grey100}`, borderRadius: T.radiusMd, padding: 14 }}>
              <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 900, color: T.grey600, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: T.fontBody }}>English</p>
              <p style={{ margin: 0, fontSize: 14, color: T.black, lineHeight: 1.65, fontFamily: T.fontBody }}>{selected.en}</p>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function QuickTourModal({ lang, onClose, onGo }) {
  const [index, setIndex] = useState(0);
  const steps = [
    {
      tab: "today",
      title: tr(lang, "tourWelcomeTitle"),
      body: tr(lang, "tourWelcomeBody"),
      accent: T.orange,
    },
    {
      tab: "today",
      title: tr(lang, "tourTodayTitle"),
      body: tr(lang, "tourTodayBody"),
      accent: T.green,
    },
    {
      tab: "orders",
      title: tr(lang, "tourOrdersTitle"),
      body: tr(lang, "tourOrdersBody"),
      accent: T.blue,
    },
    {
      tab: "inventory",
      title: tr(lang, "tourInventoryTitle"),
      body: tr(lang, "tourInventoryBody"),
      accent: T.violet,
    },
    {
      tab: "settings",
      title: tr(lang, "tourSettingsTitle"),
      body: tr(lang, "tourSettingsBody"),
      accent: T.amber,
    },
  ];
  const step = steps[index];
  const isLast = index === steps.length - 1;
  // Coding rule: Devanagari text needs the Hind font and line-height 1.8+.
  const isHi = lang === "hi";
  const tourFont = isHi ? "'Hind', 'DM Sans', sans-serif" : T.fontBody;

  useEffect(() => {
    onGo(step.tab);
  }, [step.tab, onGo]);

  const next = () => {
    if (isLast) {
      onClose();
      return;
    }
    setIndex((current) => current + 1);
  };

  return ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(26,10,0,0.52)", zIndex: 520, display: "flex", alignItems: "flex-end", justifyContent: "center", padding: 18 }}>
      <div style={{ background: T.white, borderRadius: T.radiusLg, width: "100%", maxWidth: 520, boxShadow: T.shadowLg, overflow: "hidden" }} className="dash-fade">
        <div style={{ height: 5, background: `linear-gradient(90deg, ${step.accent} ${(index + 1) / steps.length * 100}%, ${T.grey100} 0)` }} />
        <div style={{ padding: "24px 24px 20px" }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
            {steps.map((item, i) => (
              <span
                key={item.title}
                style={{ height: 7, flex: 1, borderRadius: T.radiusPill, background: i <= index ? step.accent : T.grey100 }}
              />
            ))}
          </div>
          <div style={{ width: 42, height: 42, borderRadius: 14, background: `${step.accent}18`, color: step.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 900, marginBottom: 14, fontFamily: T.fontBody }}>
            {index + 1}
          </div>
          <p style={{ fontSize: 22, lineHeight: isHi ? 1.8 : 1.2, fontWeight: 900, color: T.black, margin: "0 0 8px", fontFamily: tourFont, letterSpacing: isHi ? 0 : "-0.02em" }}>
            {step.title}
          </p>
          <p style={{ fontSize: 14, lineHeight: isHi ? 1.8 : 1.65, color: T.grey600, margin: 0, fontFamily: tourFont }}>
            {step.body}
          </p>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 24 }}>
            <button
              type="button"
              onClick={onClose}
              style={{ background: "transparent", border: "none", color: T.grey500, fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: T.fontBody, padding: "10px 0" }}
            >
              {tr(lang, "tourSkip")}
            </button>
            <div style={{ display: "flex", gap: 8 }}>
              {index > 0 && (
                <button
                  type="button"
                  onClick={() => setIndex((current) => current - 1)}
                  style={{ background: T.white, border: `1px solid ${T.grey300}`, color: T.grey600, borderRadius: T.radiusPill, padding: "10px 15px", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: T.fontBody }}
                >
                  {tr(lang, "tourBack")}
                </button>
              )}
              <button
                type="button"
                onClick={next}
                style={{ background: T.black, border: "none", color: T.white, borderRadius: T.radiusPill, padding: "10px 18px", fontSize: 13, fontWeight: 900, cursor: "pointer", fontFamily: T.fontBody }}
              >
                {isLast ? tr(lang, "tourDone") : tr(lang, "tourNext")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function SettingsTab({ storeId, lang, onLangChange }) {
  const STORE_URL = `https://kiranaos.in/store/${storeId}`;
  const [form, setForm]       = useState({ storeName: "", phone: "", address: "", city: "", hours: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState(null);
  const qrRef = useRef(null);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setSaved(false); };

  const downloadQR = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas  = document.createElement("canvas");
    canvas.width  = 200;
    canvas.height = 200;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const url  = URL.createObjectURL(blob);
    img.onload = () => {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 200, 200);
      ctx.drawImage(img, 0, 0, 200, 200);
      URL.revokeObjectURL(url);
      const a = document.createElement("a");
      a.download = "store-qr-code.png";
      a.href = canvas.toDataURL("image/png");
      a.click();
    };
    img.src = url;
  };

  useEffect(() => {
    getDoc(doc(db, "stores", storeId))
      .then((d) => {
        if (d.exists()) {
          const data = d.data();
          setForm({
            storeName: data.storeName || data.name || "",
            phone:     data.phone    || "",
            address:   data.address  || "",
            city:      data.city     || "",
            hours:     data.hours    || "",
          });
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true); setError(null); setSaved(false);
    try {
      await setDoc(doc(db, "stores", storeId), {
        storeName: form.storeName.trim(),
        phone:     form.phone.trim(),
        address:   form.address.trim(),
        city:      sanitizeCity(form.city),
        hours:     form.hours.trim(),
      }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError(tr(lang, "failedSave"));
    }
    setSaving(false);
  };

  if (loading) return <div style={S.spinner} />;

  return (
    <div style={{ maxWidth: 560 }} className="dash-fade">
      <div style={S.settingsSection}>
        <p style={S.sectionTitle}>{tr(lang, "storeInformation")}</p>

        <div style={S.formGroup}>
          <label style={S.settingLabel}>{tr(lang, "storeName")}</label>
          <input style={S.settingInput} value={form.storeName} onChange={(e) => set("storeName", e.target.value)} placeholder="e.g. Sharma Kirana" />
        </div>

        <div style={S.formGroup}>
          <label style={S.settingLabel}>{tr(lang, "phoneNumber")}</label>
          <input style={S.settingInput} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="e.g. 9999999999" />
        </div>

        <div style={S.formGroup}>
          <label style={S.settingLabel}>{tr(lang, "storeAddress")}</label>
          <input style={S.settingInput} value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Full store address" />
        </div>

        <div style={S.formGroup}>
          <label style={S.settingLabel}>{tr(lang, "storeCity")}</label>
          <input style={S.settingInput} value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="e.g. Gwalior" />
        </div>

        <div style={S.formGroup}>
          <label style={S.settingLabel}>{tr(lang, "storeHours")}</label>
          <input style={S.settingInput} value={form.hours} onChange={(e) => set("hours", e.target.value)} placeholder="Open 8AM–10PM" />
        </div>

        {error && <p style={{ color: T.red, fontSize: 13, marginBottom: 8, fontFamily: T.fontBody }}>{error}</p>}
        {saved  && <p style={{ color: T.green, fontSize: 13, marginBottom: 8, fontFamily: T.fontBody }}>{tr(lang, "settingsSaved")}</p>}

        <button style={S.saveBtn} type="button" disabled={saving} onClick={save}>
          {saving ? tr(lang, "saving") : tr(lang, "saveSettings")}
        </button>
      </div>

      {/* Language Preference section */}
      <div style={S.settingsSection}>
        <p style={S.sectionTitle}>{tr(lang, "languageSection")}</p>
        <div style={S.formGroup}>
          <label style={S.settingLabel}>{tr(lang, "chooseLanguage")}</label>
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            {[{ code: "en", label: "English" }, { code: "hi", label: "हिन्दी" }].map(({ code, label }) => (
              <button
                key={code}
                type="button"
                onClick={() => onLangChange(code)}
                style={{ flex: 1, padding: "10px 8px", borderRadius: T.radiusMd, border: `2px solid ${lang === code ? T.orange : T.grey200}`, background: lang === code ? T.orangeLight : T.white, color: lang === code ? T.orange : T.grey600, fontSize: 15, fontWeight: lang === code ? 700 : 500, fontFamily: T.fontBody, cursor: "pointer" }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* QR Code section */}
      <div style={S.qrSection}>
        <p style={S.sectionTitle}>{tr(lang, "storeQR")}</p>
        <div style={S.qrWrap}>
          <div style={S.qrBox} ref={qrRef}>
            <QRCodeSVG value={STORE_URL} size={200} bgColor="#ffffff" fgColor="#1A0A00" level="M" />
          </div>
          <p style={S.qrCaption}>{tr(lang, "scanQR")}</p>
          <button style={S.qrDownloadBtn} type="button" onClick={downloadQR}>
            {tr(lang, "downloadQR")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── StoreOwnerDashboard ──────────────────────────────────────────────────────
export default function StoreOwnerDashboard() {
  const [loggedIn,    setLoggedIn]    = useState(false);
  const [authReady,   setAuthReady]   = useState(false);
  const [storeId,     setStoreId]     = useState(null);
  const [adminMode,   setAdminMode]   = useState(false);
  const STORE_PATH = storeId ? `stores/${storeId}` : null;
  const [activeTab,   setActiveTab]   = useState("today");
  const [orderCount,  setOrderCount]  = useState(0);
  const [storeName,   setStoreName]   = useState("Demo Kirana");

  // lang: "en" | "hi" — the active dashboard language.
  // langReady: true once we have read the preference from Firestore (prevents
  //   flashing the picker before data arrives).
  // langSet: true if the merchant already has a saved preference.
  const [lang,      setLang]      = useState("en");
  const [langReady, setLangReady] = useState(false);
  const [langSet,   setLangSet]   = useState(false);
  const [tourReady, setTourReady] = useState(false);
  const [tourDone,  setTourDone]  = useState(false);
  const [showTour,  setShowTour]  = useState(false);
  const [showHelp,  setShowHelp]  = useState(false);
  const forceTour = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("tour") === "1";
  const tourAutoOpenFired = useRef(false);

  // Start each tab at the top — without this, switching tabs via the bottom
  // nav keeps the previous tab's scroll depth and users land mid-page.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  // Inject global styles
  useEffect(() => {
    const id = "dash-global-v2";
    if (!document.getElementById(id)) {
      const s = document.createElement("style");
      s.id = id;
      s.textContent = GLOBAL_CSS;
      document.head.appendChild(s);
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setAuthReady(true);
        return;
      }

      try {
        const tokenResult = await user.getIdTokenResult();
        const claims = tokenResult.claims || {};
        if (claims.role === "owner" && claims.storeId) {
          setAdminMode(false);
          setStoreId(claims.storeId);
          setLoggedIn(true);
        }
      } catch (err) {
        console.error("[KiranaOS] Failed to restore owner session:", err.message);
      } finally {
        setAuthReady(true);
      }
    });

    return () => unsub();
  }, []);

  // Live store name
  useEffect(() => {
    if (!storeId) return;
    const unsub = onSnapshot(doc(db, "stores", storeId), (snap) => {
      if (snap.exists()) {
        setStoreName(snap.data().storeName || snap.data().name || "Demo Kirana");
      }
    });
    return () => unsub();
  }, [storeId]);

  // One-time language preference load. Runs when storeId becomes available.
  // Uses getDoc (not onSnapshot) so it fires once and doesn't keep overwriting
  // language if the merchant changes it while the listener is active.
  useEffect(() => {
    if (!storeId) return;
    getDoc(doc(db, "stores", storeId))
      .then((snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const saved = data.preferredLanguage;
          if (saved === "en" || saved === "hi") {
            setLang(saved);
            setLangSet(true);
          }
          if (data.quickTourCompletedAt || data.quickTourVersion) {
            setTourDone(true);
          }
        }
        setLangReady(true);
        setTourReady(true);
      })
      .catch(() => {
        setLangReady(true);
        setTourReady(true);
      });
  }, [storeId]);

  // Persist a language choice and update local state.
  const handleLangChange = async (code) => {
    setLang(code);
    setLangSet(true);
    setTourReady(true);
    try {
      await setDoc(doc(db, "stores", storeId), { preferredLanguage: code }, { merge: true });
    } catch (e) {
      console.error("[KiranaOS] Failed to save language preference:", e.message);
    }
  };

  const completeTour = async () => {
    setShowTour(false);
    setTourDone(true);
    if (adminMode) return;
    try {
      await setDoc(doc(db, "stores", storeId), {
        quickTourCompletedAt: serverTimestamp(),
        quickTourVersion: 1,
      }, { merge: true });
    } catch (e) {
      console.error("[KiranaOS] Failed to save quick tour status:", e.message);
    }
  };

  // Live order badge count
  useEffect(() => {
    if (!loggedIn || !STORE_PATH) return;
    const unsub = onSnapshot(collection(db, `${STORE_PATH}/orders`), (snap) => {
      const n = snap.docs.filter((d) => !["delivered", "cancelled"].includes(d.data().status)).length;
      setOrderCount(n);
    });
    return () => unsub();
  }, [loggedIn, STORE_PATH]);

  useEffect(() => {
    const raw = sessionStorage.getItem("kiranaos_admin_impersonate");
    if (!raw) return;
    try {
      const payload = JSON.parse(raw);
      sessionStorage.removeItem("kiranaos_admin_impersonate");
      if (payload?.adminMode && payload?.targetStoreId) {
        setAdminMode(true);
        setStoreId(payload.targetStoreId);
        setStoreName(payload.targetStoreName || payload.targetStoreId);
        setActiveTab(payload.activeTab || "today");
        setLoggedIn(true);
      }
    } catch (e) {
      sessionStorage.removeItem("kiranaos_admin_impersonate");
    }
  }, []);

  useEffect(() => {
    if (!storeId || !langReady || !tourReady || (!adminMode && !langSet)) return;
    if (adminMode && !forceTour) return;
    if (tourAutoOpenFired.current) return; // only auto-open once per page load
    tourAutoOpenFired.current = true;
    if (tourDone && !forceTour) return;
    setShowTour(true);
  }, [adminMode, storeId, langReady, tourReady, langSet, tourDone, forceTour]);

  if (!authReady && !loggedIn) {
    return <div style={S.spinner} />;
  }

  if (!loggedIn) {
    return <LoginScreen onLogin={(storeId) => {
      setAdminMode(false);
      setStoreId(storeId);
      setLoggedIn(true);
    }} />;
  }

  // Hold the dashboard until language preference has been read from Firestore.
  // Without this guard the full dashboard renders for ~100 ms before the picker
  // appears, causing a visible flicker for first-time merchants.
  if (!adminMode && storeId && (!langReady || !tourReady)) {
    return null;
  }

  // Show language picker for first-time merchants. Skip for admin impersonation.
  if (!adminMode && langReady && !langSet) {
    return <LanguagePickerModal onSelect={handleLangChange} />;
  }

  const tabs = [
    { id: "today",     label: tr(lang, "tabToday") },
    { id: "orders",    label: tr(lang, "tabOrders"),    count: orderCount },
    { id: "analytics", label: tr(lang, "tabAnalytics") },
    { id: "inventory", label: tr(lang, "tabInventory") },
    { id: "discounts", label: tr(lang, "tabDiscounts") },
    { id: "settings",  label: tr(lang, "tabSettings")  },
  ];

  return (
    <div style={S.dashPage} className="premium-surface">
      {adminMode && <div style={S.adminBanner}>ADMIN VIEW — {storeName}</div>}

      {/* Top bar */}
      <div style={{ ...S.dashTopBar, top: adminMode ? 34 : 0, boxShadow: T.shadowSm }} className="dash-topbar-resp">
        <div style={{ minWidth: 0 }}>
          <p style={S.dashStoreName}>{storeName}</p>
          <p style={S.dashStoreMeta} className="dash-store-meta">{tr(lang, "commandCenter")}</p>
        </div>
        <div style={S.dashTopRight} className="dash-top-actions">
          <HelpButton lang={lang} onClick={() => setShowHelp(true)} />
          <TopShareButton storeId={storeId} storeName={storeName} />
          <div style={S.liveBadge} className="dash-live-badge">
            <div style={S.liveDot} />
            Live
          </div>
          <button style={S.bellBtn} type="button" aria-label="Notifications">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.grey500} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
          </button>
          <button
            className="dash-settings-gear"
            style={{ ...S.bellBtn, color: activeTab === "settings" ? T.orange : T.grey500 }}
            type="button"
            aria-label={tr(lang, "tabSettings")}
            aria-current={activeTab === "settings" ? "page" : undefined}
            onClick={() => setActiveTab("settings")}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Tab pills */}
      <div className="dash-tabs-scroll">
        {tabs.map(({ id, label, count }) => (
          <button key={id} style={S.dashTab(activeTab === id)} type="button" onClick={() => setActiveTab(id)}>
            {label}
            {count > 0 && <span style={S.dashTabBadge}>{count}</span>}
          </button>
        ))}
      </div>

      {/* Body */}
      <div style={S.dashBody} className="dash-body-resp">
        {activeTab === "today"     && <TodayTab     storeId={storeId} storeName={storeName} onGo={setActiveTab} />}
        {activeTab === "orders"    && <OrdersTab    storeId={storeId} storeName={storeName} onGo={setActiveTab} />}
        {activeTab === "analytics" && <AnalyticsTab storeId={storeId} />}
        {activeTab === "inventory" && <InventoryTab storeId={storeId} lang={lang} />}
        {activeTab === "discounts" && <DiscountsTab storeId={storeId} />}
        {activeTab === "settings"  && <SettingsTab  storeId={storeId} lang={lang} onLangChange={handleLangChange} />}
        <BrandFooter />
      </div>
      <BottomNav lang={lang} activeTab={activeTab} onGo={setActiveTab} orderCount={orderCount} />
      {!showTour && <FloatingTourButton lang={lang} onClick={() => setShowTour(true)} />}
      {showHelp && <HelpPanel lang={lang} onClose={() => setShowHelp(false)} />}
      {showTour && (
        <QuickTourModal
          lang={lang}
          onGo={setActiveTab}
          onClose={completeTour}
        />
      )}
    </div>
  );
}
